import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { getLang, getModule, getPermissions } from "store/userSlice";
import { useSelector } from "react-redux";
import { api } from "helpers/axios";
import * as yup from "yup";
import { toast } from "helpers/toast";
import * as Sentry from "@sentry/react";
import { empty, errorMessage, fieldDecimalCheck, formatDateTime, formatFixedDigits, formatMoney, formatNumberTo, getTextFromHtml, removeTrailingZeros, scrollInto } from "helpers/common";
import { en, de } from 'yup-locales';
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { selectGlobalPayfactorValues } from "store/payfactorSlice";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getGroups } from "store/groupsSlice";
import { getBudgetSettings } from "store/budgetSettingsSlice";
import { getBalanceScorecardList } from "store/balanceScorecardSlice";
import 'react-quill/dist/quill.snow.css';
import sanitize from "helpers/sanitize";
import hasPermissionTo from "helpers/permission";
import EmployeeTableColumn from "./EmployeeTableColumn";
import ErrorAlert from "components/ui/ErrorAlert";
import ScenarioExportAction from "components/BalanceScorecard/ScenarioExportAction";
import { useTranslation } from "translation/useTranslation";
import TextTruncate from "components/ui/TextTruncate";
import ExpandableHtml from "components/ui/ExpandableHtml";
import Creatable from 'react-select/creatable';
import { getAllCustomNames } from "store/fieldsSlice";
import { isDbModule } from "helpers/module";
import GenderSelect from "./inputs/GenderSelect";
import { translateGenderValue } from "./inputs/genderOptions";
import {
  POST_COMPLETION_EDITABLE_FIELDS,
  buildCompletedSettingsPayload,
  hasCompletedSettingsChanges,
} from "./completedScorecardSettings";
import EmployeeDeactivationModal from "./EmployeeDeactivationModal";
import { REFERENCE_FIELD_KEYS, buildEmployeePayload } from "./employeePayload";
import { Button, Checkbox, DateInput, Field, Input, InputAffix, Modal, NativeSelect, Select } from "components/ui";
import { finalAssignmentConfirmationDetails, withFinalAssignmentConfirmation } from "helpers/employeeDeactivation";
import { canManageEmployeeAssignments } from "helpers/employeeAssignmentAuthorization";
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);

const NORMAL_RATING = 'normal_rating';
const AVERAGE_VALUE_PERCENTILE = 'average_value_percentile';
const OPTIONAL_CALCULATION_FIELDS = ['working_days_per_week', 'individual_ceiling', 'individual_average_rating', 'individual_pay_factor'];

const READINESS_BADGE_BASE_STYLE = {
  borderRadius: '999px',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: 1.1,
  padding: '8px 12px',
  letterSpacing: '0.01em',
};

const READINESS_BADGE_STYLE = {
  present: {
    backgroundColor: '#dcefe3',
    color: '#2f6b48',
    border: '1px solid #c7e2d2',
  },
  missing: {
    backgroundColor: '#f5dddf',
    color: '#99293a',
    border: '1px solid #eec7cd',
  },
};

const getReadinessRequirementRawValue = (requirementKey, employee, scorecard) => {
  switch (requirementKey) {
    case 'scorecard.pay_factor':
    case 'scorecard.effective_pay_factor':
      return scorecard?.effective_pay_factor_resolution?.value ?? scorecard?.effective_pay_factor;
    case 'employee.annual_compensation':
      return employee?.annual_compensation;
    case 'employee.pay_grade':
      return employee?.pay_grade;
    case 'employee.pay_grade_level':
      return employee?.pay_grade_level;
    case 'employee.working_hours':
      return employee?.working_hours;
    case 'employee.employment_entry_date':
      return employee?.employment_entry_date;
    case 'employee.missing_months':
      return employee?.missing_months;
    case 'employee.missing_days':
      return employee?.missing_days;
    case 'employee.working_days_per_week':
      return employee?.working_days_per_week;
    case 'scorecard.result':
      return scorecard?.result;
    default:
      return null;
  }
};

const formatReadinessRequirementValue = (requirementKey, rawValue) => {
  if (empty(rawValue)) return '';

  if (requirementKey === 'employee.annual_compensation') {
    return formatMoney(rawValue);
  }

  if (typeof rawValue === 'number') {
    return removeTrailingZeros(String(rawValue).replace('.', ','));
  }

  if (
    typeof rawValue === 'string'
    && ['scorecard.pay_factor', 'scorecard.effective_pay_factor', 'employee.working_hours', 'employee.missing_months', 'employee.missing_days', 'employee.working_days_per_week', 'scorecard.result'].includes(requirementKey)
  ) {
    return removeTrailingZeros(rawValue.replace('.', ','));
  }

  return String(rawValue);
};

function BalancedScorecard({ index, employee, data, total, fetchEmployees, balancedScorecardOpened, setBalancedScorecardOpened, handleCancelAddBalancedScorecard, permissions, setEmployeeLkDirty, getLabel, lkWeightTotal, setShowSecondLkAlert, setShowDeleteLkAlert, isInternalTenant }) {
  const isCompleted = !!data?.evaluation_completed_at;

  const [editMode, setEditMode] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const originalValuesRef = useRef({});

  const { translate, editableTranslate, editableTranslateString } = useTranslation();
  const lang = useSelector(getLang);

  const fieldCustomNames = useSelector(getAllCustomNames);
  const budgets = useSelector(getBudgetSettings);
  const groups = useSelector(getGroups);
  const balancedScorecardList = useSelector(getBalanceScorecardList);
  const hasActiveIndividualPayFactor = Boolean(employee?.uses_individual_pay_factor)
    && Number(String(employee?.individual_pay_factor || '').replace(',', '.')) > 0;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [effectivePayFactorResolution, setEffectivePayFactorResolution] = useState(
    data?.effective_pay_factor_resolution || null
  );
  const [effectivePayFactorLoading, setEffectivePayFactorLoading] = useState(false);
  const individualPayFactorAvailable = data?.hash_id
    ? effectivePayFactorResolution?.configured_source === 'individual_pay_factor'
      && effectivePayFactorResolution?.available
    : effectivePayFactorResolution?.configured_source === 'individual_pay_factor'
      ? effectivePayFactorResolution?.available
      : hasActiveIndividualPayFactor;

  const groupOptions = useMemo(() => {
    const arr = [];
    if (employee && groups) {
      const usedGroups = employee?.balanced_scorecards?.data?.map(d => d?.group?.hash_id);

      groups.forEach(g => {
        const isUsed = ((data?.group?.hash_id !== g.hash_id) && usedGroups.includes(g.hash_id));

        arr.push({
          ...g,
          value: g.hash_id,
          label: g.name,
          isDisabled: isUsed,
          is_used: isUsed
        });
      });
    }
    return arr;
  }, [employee, groups]);

  const budgetOptions = useMemo(() => {
    if (!budgets) return [];

    return budgets.map(b => ({ value: b.hash_id, label: b.name }));
  }, [budgets]);

  const balancedScorecardSettingOptions = useMemo(() => {
    if (!balancedScorecardList) return [];

    return balancedScorecardList
      .filter(bs => !bs?.custom_config?.['balanced-scorecard-layout-v3']?.enabled)
      .map((setting) => ({
        value: setting.hash_id,
        label: setting.name,
        name: setting.name,
      }));
  }, [balancedScorecardList]);

  const schema = yup.object({
    index: yup.number().nullable(true),
    completed: yup.boolean().nullable(true),
    rating_mode: yup.string().when('completed', {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required()
    }).label(translate('field.rating_mode', 'Bewertungsmodus')),
    alternative_result: yup.string().when('rating_mode', {
      is: AVERAGE_VALUE_PERCENTILE,
      then: (schema) => schema.required()
    }).label(translate('field.alternative_result', 'Bestimmung der Durchschnittswerts')),
    average_value_individual: yup.string().when('alternative_result', {
      is: 'average_value_individual',
      then: (schema) => schema.required()
        .test('number-range', translate('form.valid_range') + ' 0-999,999', value => {
          const temp = Number(formatNumberTo(value, '.'));
          return temp > 0 && temp <= 999.999;
        })
        .test('is-decimal', translate('form.valid_format_decimal_3'), value => !value ? true : (value.toString()).match(/^\d*(,{1}(\d{1,3}))?$/)),
      otherwise: (schema) => schema.notRequired()
    }).label(translate('field.alternative_result.average_value_individual', 'Durchschnittswert (individueller Wert)')),
    balanced_scorecard_setting_hash_id: yup.string().when(['rating_mode', 'completed'], {
      is: (ratingMode, completed) => completed || ratingMode === AVERAGE_VALUE_PERCENTILE,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required()
    }).label(translate('field.balanced_scorecard_template', 'LK-Vorlage')),
    budget_setting_hash_id: yup.string().notRequired()
      .label(fieldCustomNames?.['field.budget'] || translate('field.budget', 'Budget')),
    group_hash_id: yup.string().notRequired()
      .label(fieldCustomNames?.['field.group'] || translate('field.group', 'Gruppe')),
    pay_factor_source: yup.string().oneOf(['calculation_setting', 'individual_pay_factor', 'custom']).test(
      'individual-pay-factor-available',
      translate('balanced_scorecard.individual_pay_factor_required', 'Der individuelle Entgeltfaktor muss in den Mitarbeiterdaten ausgefüllt sein.'),
      function (value) {
        return value !== 'individual_pay_factor'
          || individualPayFactorAvailable;
      }
    ).notRequired(),
    pay_factor: yup.string().transform(cv => cv ? cv : '').when('pay_factor_source', {
      is: (payFactorSource) => payFactorSource === 'custom',
      then: (schema) => schema.required().test('positive-number', translate('form.positive_number_required', 'Bitte einen positiven Wert eingeben.'), (value) => Number(String(value).replace(',', '.')) > 0),
      otherwise: (schema) => schema.notRequired()
    }).test('is-decimal', translate('form.valid_format_decimal_3'), value => !value ? true : (value.toString()).match(/^\d*(,{1}(\d{1,3}))?$/)).label(fieldCustomNames?.['field.pay_factor'] || translate('field.pay_factor', 'Entgeltfaktor')),
    fixed_payout: yup.string().test('number-range', translate('form.valid_range') + ' 0-999999,999', value => {
      const temp = Number(formatNumberTo(value, '.'));
      return temp >= 0 && temp <= 999999.999;
    }).test('is-decimal', translate('form.valid_format_decimal_3'), value => !value ? true : (value.toString()).match(/^\d*(,{1}(\d{1,3}))?$/)).label(fieldCustomNames?.['field.fixed_payout'] || translate('field.fixed_payout', 'Festbetrag')),
    distribution_percentage: yup.string().when('index', {
      is: (val) => (val === null || val === 0),
      then: (schema) => schema.nullable(true),
      otherwise: (schema) => schema.required()
        .test('number-range', translate('form.valid_range') + ' 1-99', value => {
          const temp = Number(formatNumberTo(value, '.'));
          return temp >= 1 && temp <= 99;
        })
        .test('is-decimal', translate('form.valid_format_decimal_2'), value => !value ? true : (value.toString()).match(/^\d*(,{1}(\d{1,2}))?$/))
        .label(fieldCustomNames?.['field.weighting'] || translate('field.weighting', 'Gewichtung')),
    })
  });

  const { register, getValues, setValue, reset, watch, handleSubmit, control, formState: { isDirty, errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const watchIndex = watch('index');

  const watchRatingMode = watch('rating_mode');
  const isAverageRatingMode = watchRatingMode === AVERAGE_VALUE_PERCENTILE;
  const watchAlternativeResult = watch('alternative_result');
  const watchBudgetSettingHashId = watch('budget_setting_hash_id');
  const watchPayFactorSource = watch('pay_factor_source');
  const watchPayFactor = watch('pay_factor');
  const watchGroupHashId = watch('group_hash_id');
  const watchBalancedScorecardSettingHashId = watch('balanced_scorecard_setting_hash_id');
  const selectedBudget = useMemo(() => {
    if (!watchBudgetSettingHashId || !budgets) return null;
    return budgets.find((item) => item.hash_id === watchBudgetSettingHashId) || null;
  }, [budgets, watchBudgetSettingHashId]);

  useEffect(() => {
    const shouldResolve = editMode
      && employee?.hash_id
      && (
        watchPayFactorSource === 'calculation_setting'
        || watchPayFactorSource === 'individual_pay_factor'
      );

    if (!shouldResolve) {
      setEffectivePayFactorLoading(false);
      return undefined;
    }

    let cancelled = false;
    const params = {};
    const budgetHashId = watchBudgetSettingHashId;

    if (
      watchPayFactorSource === 'calculation_setting'
      && !budgetHashId
    ) {
      setEffectivePayFactorResolution(null);
      setEffectivePayFactorLoading(false);
      return undefined;
    }

    if (budgetHashId) params.budget_setting_hash_id = budgetHashId;
    params.pay_factor_source = watchPayFactorSource;
    setEffectivePayFactorResolution(null);
    setEffectivePayFactorLoading(true);

    const endpoint = data?.hash_id
      ? '/employee-balanced-scorecards/' + data.hash_id + '/effective-pay-factor'
      : '/employees/' + employee.hash_id + '/effective-pay-factor';

    api.get(endpoint, { params })
      .then(({ data: response }) => {
        if (!cancelled) setEffectivePayFactorResolution(response?.data || null);
      })
      .catch((err) => {
        if (!cancelled) setEffectivePayFactorResolution(null);
        Sentry.captureException(err);
      })
      .finally(() => {
        if (!cancelled) setEffectivePayFactorLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    editMode,
    data?.hash_id,
    employee?.hash_id,
    watchBudgetSettingHashId,
    watchPayFactorSource,
  ]);

  const selectedBudgetForReadiness = useMemo(() => {
    if (!budgets) return null;

    const selectedBudgetHashId = editMode
      ? watchBudgetSettingHashId
      : data?.budget_setting?.hash_id;

    if (!selectedBudgetHashId) return null;

    return budgets.find((item) => item.hash_id === selectedBudgetHashId) || null;
  }, [budgets, data?.budget_setting?.hash_id, editMode, watchBudgetSettingHashId]);

  const readinessRequirements = useMemo(() => {
    const requirements = selectedBudgetForReadiness?.calculation_model?.readiness_requirements;
    return Array.isArray(requirements) ? requirements : [];
  }, [selectedBudgetForReadiness]);

  const readinessBadges = useMemo(() => {
    if (
      editMode
      || !data
      || employee?.exclude_from_payout
      || data?.exclude_from_payout
      || readinessRequirements.length < 1
    ) {
      return [];
    }

    return readinessRequirements.map((requirement) => {
      const key = requirement?.key;
      const rawValue = getReadinessRequirementRawValue(key, employee, data);
      const isEffectivePayFactor = ['scorecard.pay_factor', 'scorecard.effective_pay_factor'].includes(key);
      const resolutionAvailable = data?.effective_pay_factor_resolution?.available;
      const missing = isEffectivePayFactor && typeof resolutionAvailable === 'boolean'
        ? !resolutionAvailable
        : empty(rawValue);
      const formattedValue = formatReadinessRequirementValue(key, rawValue);
      let label = key;

      switch (key) {
        case 'scorecard.pay_factor':
        case 'scorecard.effective_pay_factor':
          label = fieldCustomNames?.['field.pay_factor'] || translate('field.pay_factor', 'Entgeltfaktor');
          break;
        case 'employee.annual_compensation':
          label = fieldCustomNames?.['field.annual_compensation'] || translate('field.annual_compensation', 'Jahresentgelt');
          break;
        case 'employee.pay_grade':
          label = fieldCustomNames?.['field.pay_grade'] || translate('field.pay_grade', 'Entgeltgruppe');
          break;
        case 'employee.pay_grade_level':
          label = fieldCustomNames?.['field.pay_grade_level'] || translate('field.pay_grade_level', 'Stufe');
          break;
        case 'employee.working_hours':
          label = fieldCustomNames?.['field.working_hours'] || translate('field.working_hours', 'Arbeitszeit');
          break;
        case 'employee.employment_entry_date':
          label = fieldCustomNames?.['field.employment_entry_date'] || translate('field.employment_entry_date', 'Eintrittsdatum');
          break;
        case 'employee.missing_months':
          label = fieldCustomNames?.['field.missing_months'] || translate('field.missing_months', 'Fehlmonate');
          break;
        case 'employee.missing_days':
          label = fieldCustomNames?.['field.missing_days'] || translate('field.missing_days', 'Fehltage');
          break;
        case 'employee.working_days_per_week':
          label = fieldCustomNames?.['field.working_days_per_week'] || translate('field.working_days_per_week', 'individuelle Arbeitstage');
          break;
        case 'scorecard.result':
          label = fieldCustomNames?.['field.result'] || translate('field.result', 'Gesamturteil');
          break;
      }

      return {
        key,
        label,
        missing,
        formattedValue,
      };
    }).filter((badge) => !!badge.key);
  }, [data, editMode, employee, readinessRequirements, translate, fieldCustomNames]);

  const defaultPayFactorSourceText = useMemo(() => {
    const payFactorBudget = selectedBudget;

    if (!payFactorBudget) {
      return translate('balanced_scorecard.pay_factor_mode.auto_fallback', 'Der automatische Wert wird aus dem ausgewählten Berechnungsmodell übernommen.');
    }

    const calculationMethod = payFactorBudget?.calculation_model?.calculation_method;
    const pointValueType = payFactorBudget?.calculation_model?.point_value_type;
    const pointValueModelType = payFactorBudget?.calculation_model?.point_value_flexible_type;
    const payFactorTable = payFactorBudget?.calculation_model?.point_value_flexible_type_use_table_select;

    if (calculationMethod === 'point_value' && pointValueType === 'flexible') {
      if (pointValueModelType === 'annual_fee') {
        return translate('balanced_scorecard.pay_factor_mode.auto_annual_fee', 'Automatische Quelle: Jahresentgelt.');
      }

      if (pointValueModelType === 'use_table') {
        const tableLabel = payFactorTable?.label ? ` (${payFactorTable.label})` : '';
        return translate('balanced_scorecard.pay_factor_mode.auto_use_table', 'Automatische Quelle: Tabelle für Entgeltgruppe / Stufe.') + tableLabel;
      }

      if (pointValueModelType === 'no_differentiation') {
        return translate('balanced_scorecard.pay_factor_mode.auto_no_differentiation', 'Automatische Quelle: fester Wert 1 (keine Differenzierung).');
      }

    }

    return translate('balanced_scorecard.pay_factor_mode.auto_fallback', 'Der automatische Wert wird aus dem ausgewählten Berechnungsmodell übernommen.');
  }, [selectedBudget, translate]);

  const calculationSettingPayFactorSourceText = useMemo(() => {
    if (effectivePayFactorResolution?.effective_source === 'individual_pay_factor') {
      return translate('balanced_scorecard.pay_factor_mode.auto_individual_override', 'Automatische Quelle: aktiver individueller Entgeltfaktor.');
    }

    return defaultPayFactorSourceText;
  }, [defaultPayFactorSourceText, effectivePayFactorResolution?.effective_source, translate]);

  const hasDisruptiveChanges = (formData) => {
    return hasCompletedSettingsChanges(formData, originalValuesRef.current);
  };

  const handleAbortChange = () => {
    reset({
      ...getValues(),
      ...POST_COMPLETION_EDITABLE_FIELDS.reduce((values, field) => {
        values[field] = originalValuesRef.current[field];
        return values;
      }, {}),
    });
    setPendingFormData(null);
    setConfirmModalOpen(false);
  };

  const handleConfirmChange = () => {
    if (!pendingFormData) return;
    setConfirmModalOpen(false);
    handleSubmitBalancedScorecardAction(pendingFormData);
  };

  const onSubmitWithConfirm = (formData) => {
    if (isCompleted && hasDisruptiveChanges(formData)) {
      setPendingFormData(formData);
      setConfirmModalOpen(true);
      return;
    }
    handleSubmitBalancedScorecardAction(formData);
  };

  useEffect(() => {
    setEmployeeLkDirty(isDirty);
  }, [isDirty]);

  useEffect(() => {
    // Only clear a held value: writing '' into a pristine form (view mode, no reset yet)
    // makes it differ from its defaults, so the card counts as dirty and blocks navigation.
    if (!isAverageRatingMode && getValues('alternative_result')) {
      setValue('alternative_result', '');
    }
  }, [isAverageRatingMode, getValues, setValue]);

  useEffect(() => {
    if (isDirty && watchPayFactorSource !== 'custom' && watchPayFactor) {
      setValue('pay_factor', '');
    }
  }, [watchPayFactorSource, watchPayFactor, isDirty, setValue]);

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    if (employee && !data) { // when adding a new balanced scorecard
      handleShowEditMode();
    }
  }, [data]);

  useEffect(() => {
    if (!watchGroupHashId) return;

    // remove all warning messages
    removeAllWarnings();

    // if group is changed, show warning message
    if (data?.group?.hash_id && data.group.hash_id !== watchGroupHashId) {
      // insert warning message into the DOM
      const warningMessage = document.createElement('div');
      warningMessage.className = 'alert alert-warning';
      warningMessage.id = `group-changed-warning-${data.hash_id}`;
      warningMessage.innerHTML = editableTranslateString('balanced_scorecard.group_changed_warning', 'Wird die Leistungskarte einer anderen Gruppe zugewiesen, können die Führungskräfte der neuen Gruppe alle bisherigen Änderungen einsehen. Wenn dies nicht gewünscht ist, muss die Leistungskarte gelöscht und neu erstellt werden.');
      const balancedScorecardElement = document.getElementById(`employee-balanced-scorecard-${data.hash_id}`);
      if (balancedScorecardElement) {
        balancedScorecardElement.prepend(warningMessage);
      }
    }

  }, [watchGroupHashId]);

  const removeAllWarnings = () => {
    // remove all warning messages
    const warningMessages = document.querySelectorAll(`[id^="group-changed-warning-"]`);
    if (warningMessages.length > 0) {
      warningMessages.forEach((message) => {
        message.remove();
      });
    }
  };

  const handleShowEditMode = async (e = null) => {
    e?.preventDefault();

    removeAllWarnings();

    if (balancedScorecardOpened) return;

    if (data?.hash_id && !await postLock(data?.hash_id)) return;

    setBalancedScorecardOpened(true);

    const initialValues = {
      index,
      completed: (data?.evaluation_completed_at ? true : false),
      rating_mode: data?.rating_mode === AVERAGE_VALUE_PERCENTILE ? AVERAGE_VALUE_PERCENTILE : NORMAL_RATING,
      // A stale value from normal rating mode would still be validated and submitted.
      alternative_result: data?.rating_mode === AVERAGE_VALUE_PERCENTILE ? (data?.alternative_result || '') : '',
      average_value_individual: data?.average_value_individual || null,
      hash_id: data?.hash_id || '',
      employee_hash_id: employee.hash_id,
      balanced_scorecard_setting_hash_id: data?.balanced_scorecard_setting?.hash_id || '',
      budget_setting_hash_id: data?.budget_setting?.hash_id || '',
      group_hash_id: data?.group?.hash_id || '',
      pay_factor_source: data?.pay_factor_source || 'calculation_setting',
      pay_factor: data?.pay_factor || '',
      distribution_percentage: data?.distribution_percentage || '',
      fixed_payout: data?.fixed_payout || '',
    };

    reset(initialValues);

    originalValuesRef.current = {
      budget_setting_hash_id: initialValues.budget_setting_hash_id,
      pay_factor_source: initialValues.pay_factor_source,
      pay_factor: initialValues.pay_factor,
      fixed_payout: initialValues.fixed_payout,
      distribution_percentage: initialValues.distribution_percentage
    };
    setPendingFormData(null);
    setConfirmModalOpen(false);

    setEditMode(true);
  };

  const handleSubmitBalancedScorecardAction = async (formData) => {
    setError(null);
    setLoading(true);

    removeAllWarnings();

    try {
      if (isCompleted) {
        const completedSettingsPayload = buildCompletedSettingsPayload(formData);

        const { status, data: responseData } = await api.patch(
          `/employee-balanced-scorecards/${data.hash_id}/completed-calculation-settings`,
          completedSettingsPayload,
        );

        if (status === 200) {
          const recalculationStarted = responseData?.recalculation?.status === 'queued';
          toast.success(recalculationStarted
            ? translate(
              'balanced_scorecard.completed_changes_recalculation_started',
              'Änderungen gespeichert. Die Neuberechnung der betroffenen Budgets und Auszahlungen wurde gestartet.'
            )
            : translate(
              'balanced_scorecard.completed_changes_saved_no_recalculation',
              'Änderungen gespeichert. Keine abgeschlossene Auszahlungskalkulation erforderte eine Neuberechnung.'
            ));
          await handleCancel();
          await fetchEmployees(false, false);
        }

        setLoading(false);
        return;
      }

      const payload = {
        ...formData,
        uses_employee_reference_defaults: false,
        budget_setting_hash_id: formData.budget_setting_hash_id || null,
        group_hash_id: formData.group_hash_id || null,
      };

      if (formData.rating_mode === AVERAGE_VALUE_PERCENTILE) {
        delete payload.balanced_scorecard_setting_hash_id;
      }

      const { status } = await api.post('/employee-balanced-scorecards', payload);
      if (status === 200) {
        if (total === 2 && !formData?.hash_id) {
          setShowDeleteLkAlert(false);
          setShowSecondLkAlert(true);
        } else {
          setShowDeleteLkAlert(false);
          setShowSecondLkAlert(false);
        }
        toast.success(translate('form.success.saved'));
        await handleCancel();
        fetchEmployees(false, false);
        scrollInto('.selected-employee-row', false, -100);
      }
      setLoading(false);
    } catch (err) {
      if (err?.response?.data?.message?.includes('Weighting error')) {
        setError({ translationKey: 'balance_scorecard.weighting_error' });
      } else {
        setError(errorMessage(err, 'save balanced scorecard setting'));
      }
      setLoading(false);
      Sentry.captureException(err);
    };
  };

  const handleReleaseBalancedScorecard = async () => {
    const formData = {
      completed: false
    };

    setError(null);
    setLoading(true);

    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${data?.hash_id}/evaluation`, formData);
      if (status === 200) {
        toast.success(translate('form.success.saved'));

        await fetchEmployees(false, false);
        await postUnlock(data?.hash_id);
      }
      setLoading(false);
    } catch (err) {
      setError(errorMessage(err, 'save balanced scorecard setting'));
      setLoading(false);
      Sentry.captureException(err);
    };
  };

  const handleCancel = async () => {
    if (!data) handleCancelAddBalancedScorecard();
    else {
      await postUnlock(data?.hash_id);
    }
    setPendingFormData(null);
    setConfirmModalOpen(false);
    setEditMode(false);
    setBalancedScorecardOpened(false);
  };

  const onDeleteBalancedScorecard = async (hash_id) => {
    if (!await postLock(hash_id)) return;

    setError(null);

    try {
      const { status } = await api.delete(`/employee-balanced-scorecards/${hash_id}`);
      if (status === 200) {
        toast.success(translate('form.success.saved'));

        fetchEmployees(false, false);
        setShowSecondLkAlert(false);
        setShowDeleteLkAlert(total === 1 ? false : true);

        scrollInto('.selected-employee-row', false, -100);
      }
    } catch (err) {
      setError(errorMessage(err, 'delete balanced scorecard'));
      Sentry.captureException(err);
    }
  };

  const postLock = async (hashId) => {
    setError(null);
    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${hashId}/lock`);
      if (status === 200) return true;
    } catch (err) {
      if (err?.response?.status === 403) {
        let formatted = translate('evaluation.balanced_scorecard_lock_forbidden');
        if (err?.response?.data) {
          const errData = err?.response?.data;
          if (errData?.data?.evaluator) {
            formatted = formatted.replace('$X', '<b>' + errData?.data?.evaluator?.name + '</b>');
          }
        }
        setError(formatted);
      } else {
        setError(errorMessage(err, 'lock employee balanced scorecard'));
      }
    }
  };

  const postUnlock = async (hashId) => {
    setError(null);
    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${hashId}/unlock`);
      if (status === 200) return true;
    } catch (err) {
      if (err?.response?.status === 403) {
        let formatted = translate('evaluation.balanced_scorecard_lock_forbidden');
        if (err?.response?.data) {
          const errData = err?.response?.data;
          if (errData?.data?.evaluator) {
            formatted = formatted.replace('$X', '<b>' + errData?.data?.evaluator?.name + '</b>');
          }
        }
        setError(formatted);
      } else {
        setError(errorMessage(err, 'unlock employee balanced scorecard'));
      }
    }
  };

  const handleUnlockComplaint = async (hashId) => {
    setError(null);

    try {
      const { status } = await api.post(`/employee-complaints/${hashId}/unlock`);
      if (status === 200) {
        toast.success(translate('form.success.saved'));

        await fetchEmployees(false, false);
      }
    } catch (err) {
      setError(errorMessage(err, 'unlock employee complaint'));
      Sentry.captureException(err);
    }
  };

  const onSubmitScorecard = (formData) => {
    onSubmitWithConfirm(formData);
  };

  return <div className={`employee-balancescorecard-card position-relative ${data?.evaluation_completed_at ? 'completed' : ''} ${data?.exclude_from_payout ? 'exclude-from-payout' : ''} mb-3`} data-tooltip-id={employee.exclude_from_payout ? `employee-exclusion-tooltip` : (data?.exclude_from_payout ? `balanced-scorecard-exclusion-tooltip` : '')}>
    <ReactTooltip id={`employee-exclusion-tooltip`} content={translate('tooltips.balanced_scorecard.employee_exclude_from_payout_tooltip')} />
    <ReactTooltip id={`balanced-scorecard-exclusion-tooltip`} content={translate('tooltips.balanced_scorecard.exclude_from_payout_tooltip')} />
    <form onSubmit={handleSubmit(onSubmitScorecard)} data-testid="scorecard-form">
      <div className="card-setting-header d-flex justify-content-between align-items-center">
        <span>
          {editableTranslate('employee.balanced_scorecard')} {index + 1} {(data?.distribution_percentage && (data?.distribution_percentage !== '100' || lkWeightTotal !== 100)) && <span className="font-weight-normal">({data?.distribution_percentage}%)</span>}
          {
            data?.rating_mode === 'average_value_percentile' && <>
              <i className="bi bi-slash-circle-fill green-color ml-2 mr-1" data-tooltip-id="lk-average_value_percentile-tooltip"></i>
              <ReactTooltip id={`lk-average_value_percentile-tooltip`} place="bottom" content={translate('tooltips.evaluation.lk.average_tooltip')} />
            </>
          }
          {
            data && <sup>
              <i className="bi bi-clock ml-1" data-tooltip-id="lk-last-updated-tooltip" data-tooltip-content={`${translate('tooltips.balanced_scorecard.last_updated')} : ${data?.has_evaluation_started ? formatDateTime(data?.updated_at) : '-'}`}></i>
            </sup>
          }
        </span>
        {
          (!editMode && data) ? <div>
            {
              !data.exclude_from_payout && <>
                {
                  data.evaluation_completed_at ? <>
                    <i className="bi bi-check-circle-fill v-middle mr-2" data-tooltip-id={`balanced-scorecard-completed-${data.hash_id}`}></i>
                    <ReactTooltip id={`balanced-scorecard-completed-${data.hash_id}`} place="bottom" content={translate('tooltips.evaluation.lk.completed_tooltip') + ' (' + formatDateTime(data.evaluation_completed_at) + ')'} />

                    {
                      hasPermissionTo('employee_management.manage_balanced_scorecards.release', permissions) && <Button variant="ghost" size="inline" icon="bi-unlock" loading={loading} id={`release-balanced-scorecard-${data.hash_id}`} data-tooltip-id="release-balanced-scorecard-tooltip" data-testid="release-button" />
                    }

                    <ReactTooltip id="release-balanced-scorecard-tooltip" content={translate('tooltips.balanced_scorecard.release_button')} />

                    {
                      !loading && <ReactTooltip anchorSelect={`#release-balanced-scorecard-${data.hash_id}`} place="bottom" openOnClick={true} clickable>
                        <div style={{ maxWidth: '380px', whiteSpace: 'normal' }} className="p-1">
                          <div className="mb-2 font-weight-bold">
                            {editableTranslate('balanced_scorecard.reopen_evaluation_title', 'Bewertung wieder öffnen?')}
                          </div>
                          <div className="mb-3">
                            {editableTranslate('balanced_scorecard.reopen_evaluation_confirmation', 'Beim Wiederöffnen wird der Abschlussstatus zurückgesetzt. Bereits eingegebene Bewertungsdaten bleiben erhalten. Der gespeicherte Stammdaten-Kontext wird entfernt und die Karte verwendet wieder die aktuellen Beschäftigtendaten. Möchten Sie fortfahren?')}
                          </div>
                          <Button variant="primary" size="sm" onClick={handleReleaseBalancedScorecard} data-testid="reopen-button" className="font-weight-normal">{editableTranslate('button.reopen', 'Wieder öffnen')}</Button>
                        </div>
                      </ReactTooltip>
                    }
                  </> :
                    <>
                      {
                        hasPermissionTo('employee_management.manage_balanced_scorecards.delete', permissions) && <Button variant="ghost" size="inline" tone="danger" icon="bi-trash" id={`delete-balanced-scorecard-${data.hash_id}`} className={!hasPermissionTo('employee_management.manage_balanced_scorecards.create', permissions) ? 'mr-2' : ''} data-testid="delete-button" />
                      }

                      <ReactTooltip className="opacity-1" anchorSelect={`#delete-balanced-scorecard-${data.hash_id}`} place="bottom" openOnClick={true} clickable>
                        <Button variant="primary" size="sm" onClick={() => onDeleteBalancedScorecard(data.hash_id)} data-testid="confirm-button" className="font-weight-normal">{editableTranslate('form.delete.yes')}</Button>
                      </ReactTooltip>
                    </>
                }
                <ScenarioExportAction
                  isInternalTenant={isInternalTenant}
                  balancedScorecardHashId={data?.hash_id}
                  settingName={data?.balanced_scorecard_setting?.name}
                />
                {
                  hasPermissionTo('employee_management.manage_balanced_scorecards.create', permissions) && <Button variant="ghost" size="inline" icon="bi-pencil-square" onClick={handleShowEditMode} data-testid="edit-button" />
                }
              </>
            }
          </div> : <div>
            <Button variant="secondary" onClick={handleCancel} data-testid="cancel-button" className="mr-2">
              <span className="hidden-xl">{editableTranslate('button.cancel')}</span>
              <i className="bi bi-x-lg text-normal show-xl" data-tooltip-id="form-cancel-tooltip"></i>
            </Button>
            <Button type="submit" variant="primary" loading={loading} data-testid="save-button"><>
                    <span className="hidden-xl">{editableTranslate('button.save')}</span>
                    {!loading && <i className="bi bi-floppy text-normal show-xl" data-tooltip-id="form-save-tooltip"></i>}
                  </></Button>
          </div>
        }
      </div>
      <hr className="mt-2" />
      <div className={`card-setting-body mt-3 ${!editMode && data ? 'pb-3' : ''}`} id={`employee-balanced-scorecard-${data?.hash_id}`}>
        {
          error && <div className="pb-2">
            <ErrorAlert error={error} isHtml={true} onClose={() => setError(null)} />
          </div>
        }
        {
          (!editMode && data) ? <div className="row">
            {
              data?.rating_mode !== 'average_value_percentile' && <div className="col-12 d-flex justify-content-between p-0">
                <div className="col card-body-key">{fieldCustomNames?.['field.balanced_scorecard_template'] || editableTranslate('field.balanced_scorecard_template')}</div>
                <div className="col card-body-value use-ellipsis"><span data-tooltip-id={`balanced-scorecard-name-${index}`}>{data?.balanced_scorecard_setting?.name || '-'}</span></div>
                {
                  data?.balanced_scorecard_setting?.name && <ReactTooltip id={`balanced-scorecard-name-${index}`} place="bottom" content={data?.balanced_scorecard_setting?.name} />
                }
              </div>
            }
            <div className="col-12 d-flex justify-content-between p-0">
              <div className="col card-body-key">{fieldCustomNames?.['field.budget'] || editableTranslate('field.budget')}</div>
              <div className="col card-body-value">{data?.budget_setting?.name || <i className="bi bi-exclamation-triangle-fill text-normal orange-color" data-tooltip-id="no-budget-tooltip"></i>}</div>
            </div>
            <div className="col-12 d-flex justify-content-between p-0">
              <div className={`col card-body-key ${!data.complaints || data.complaints.data.length === 0 ? 'mb-0' : ''}`}>{fieldCustomNames?.['field.group'] || editableTranslate('field.group')}</div>
              <div className={`col card-body-value ${!data.complaints || data.complaints.data.length === 0 ? 'mb-0' : ''}`}>{data?.group?.name || <i className="bi bi-exclamation-triangle-fill text-normal orange-color" data-tooltip-id={'no-group-tooltip'}></i>}</div>
            </div>
            {
              data.complaints && data.complaints.data.map((complaint, i) => <Fragment key={i}>
                <div className="col-10 d-flex justify-content-between p-0">
                  <div className={`col card-body-key ${i === data.complaints.data.length - 1 ? 'mb-0' : ''}`}>{editableTranslate('field.complaint', 'Beschwerde')}</div>
                  <div className={`col card-body-value ${i === data.complaints.data.length - 1 ? 'mb-0' : ''}`}>
                    <TextTruncate text={getTextFromHtml(sanitize(complaint?.complaint_notes))} className="pr-5" />
                  </div>
                </div>
                <div className="col-2 text-right">
                  {
                    complaint.is_resolved && <i className="bi bi-check-circle-fill v-middle green-color" data-tooltip-id={`complaint-resolved-tooltip`} data-tooltip-content={translate('tooltips.complaint.resolved_at', 'Beschwerde beigelegt am') + ' ' + formatDateTime(complaint.resolved_at, true)} />
                  }
                  {
                    !complaint.is_resolved && complaint.is_locked && <Button variant="ghost" size="inline" className="text-default" icon="bi-unlock" data-tooltip-id={`complaint-unlock`} onClick={() => handleUnlockComplaint(complaint.hash_id)} data-testid={`unlock-complaint-${complaint.hash_id}`} />
                  }
                  {
                    // !complaint.is_resolved && !complaint.is_locked && <span className={`text-default font-weight-bold py-0`}><i className="bi bi-unlock"></i></span>
                  }
                </div>
              </Fragment>)
            }
            {
              readinessBadges.length > 0 && <div className="col-12 mt-1 p-0">
                <div className="px-3 pb-2">
                  <hr className="mb-2 mt-2" />
                  <div className="small text-muted font-weight-bold mb-2">
                    {editableTranslate('balanced_scorecard.readiness_badges_title', 'Berechnungsrelevante Felder')}
                  </div>
                  <div className="d-flex flex-wrap">
                    {
                      readinessBadges.map((badge) => {
                        const tooltipId = `readiness-${data.hash_id}-${badge.key.replace(/\./g, '-')}`;
                        const missingTooltipId = `${tooltipId}-missing`;
                        const badgeStyle = badge.missing
                          ? READINESS_BADGE_STYLE.missing
                          : READINESS_BADGE_STYLE.present;

                        if (badge.missing) {
                          return (
                            <Fragment key={badge.key}>
                              <span
                                className="badge badge-pill mr-1 mb-1"
                                style={{ ...READINESS_BADGE_BASE_STYLE, ...badgeStyle }}
                                data-tooltip-id={missingTooltipId}
                              >
                                {badge.label}
                              </span>
                              <ReactTooltip
                                id={missingTooltipId}
                                place="bottom"
                              >
                                {editableTranslate('balanced_scorecard.readiness_badges_missing_tooltip', 'Pflichtfeld. Die Berechnung schlägt fehl, wenn kein Wert vorhanden ist.')}
                              </ReactTooltip>
                            </Fragment>
                          );
                        }

                        return <Fragment key={badge.key}>
                          <span
                            className="badge badge-pill mr-1 mb-1"
                            style={{ ...READINESS_BADGE_BASE_STYLE, ...badgeStyle }}
                            data-tooltip-id={tooltipId}
                          >
                            {badge.label}
                          </span>
                          <ReactTooltip
                            id={tooltipId}
                            place="bottom"
                            content={`${badge.label}: ${badge.formattedValue}`}
                          />
                        </Fragment>;
                      })
                    }
                  </div>
                </div>
              </div>
            }
          </div> : <div>
            <div className="form-row align-items-start">
              {
                isCompleted && <div className="form-group col-12">
                  <div className="alert alert-info d-flex align-items-start mb-0" role="status">
                    <i className="bi bi-info-circle-fill mr-2 mt-1" aria-hidden="true"></i>
                    <div>
                      {editableTranslate(
                        'balanced_scorecard.completed_partial_edit_info',
                        'Diese Leistungskarte ist abgeschlossen. Einstellungen zur Berechnung können weiterhin geändert werden. Beurteilungsbezogene Felder sind schreibgeschützt und erfordern das erneute Öffnen der Leistungskarte. Beim Speichern von Änderungen werden betroffene Budgets und Auszahlungen möglicherweise neu berechnet.'
                      )}
                    </div>
                  </div>
                </div>
              }
              <div className="form-group col-6">
                <Field label={getLabel('field.rating_mode', 'Rating Mode', null, true)} error={errors?.rating_mode?.message}>
                <NativeSelect
                  error={!!errors?.rating_mode}
                  {...register('rating_mode')}
                  disabled={isCompleted}
                  title={isCompleted ? translate('balanced_scorecard.completed_field_requires_reopen', 'Öffnen Sie die Leistungskarte erneut, um dieses Feld zu ändern.') : undefined} data-testid="rating-mode-select"
                >
                  <option value={NORMAL_RATING}>{translate('field.rating_mode.normal_rating', 'Bewertung')}</option>
                  <option value={AVERAGE_VALUE_PERCENTILE}>{translate('field.rating_mode.average_value_percentile', 'Durchschnittswert')}</option>
                </NativeSelect>
                </Field>
              </div>
              {
                watchRatingMode === 'average_value_percentile' && <div className="form-group col-6">
                  <Field label={getLabel('field.alternative_result', 'Alternative Result', null, true)} error={errors?.alternative_result?.message}>
                  <NativeSelect error={!!errors?.alternative_result} {...register('alternative_result')} disabled={isCompleted} data-testid="alternative-result-select">
                    <option value="">{translate('form.select_placeholder')}</option>
                    <option value="average_value_calculated">{translate('field.alternative_result.average_value_calculated', 'Durchschnittswert (berechnet)')}</option>
                    <option value="average_value_individual">{translate('field.alternative_result.average_value_individual', 'Durchschnittswert (individueller Wert)')}</option>
                  </NativeSelect>
                  </Field>
                </div>
              }
              {
                watchAlternativeResult === 'average_value_individual' && <div className="form-group col-6">
                  <Field label={getLabel('field.alternative_result.average_value_individual', 'Average Value (Individual)', null, true)} error={errors?.average_value_individual?.message}>
                    <Input type="text" error={!!errors?.average_value_individual} {...register('average_value_individual')} maxLength={7} readOnly={isCompleted} data-testid="average-value-individual-input" />
                  </Field>
                </div>
              }
              {
                !isAverageRatingMode && <div className="form-group col-6">
                  <Field label={getLabel('field.balanced_scorecard_template', 'Balanced Scorecard Template', fieldCustomNames?.['field.balanced_scorecard_template'], true)} error={errors?.balanced_scorecard_setting_hash_id?.message}>
                  <>
                        <div {...(isCompleted || data?.has_evaluation_started) ? { 'data-tooltip-id': `bs-balanced_scorecard-template-locked-tooltip` } : {}}>
                          <Controller
                            name={'balanced_scorecard_setting_hash_id'}
                            control={control}
                            render={({ field: f }) =>
                              <div style={{ display: 'contents' }} data-testid="balanced-scorecard-setting-hash-id-select">
                                <Select
                                  {...f}
                                  error={!!errors?.balanced_scorecard_setting_hash_id}
                                  options={balancedScorecardSettingOptions}
                                  value={balancedScorecardSettingOptions.find((o) => o.value === watchBalancedScorecardSettingHashId) || null}
                                  onChange={(opt) => f.onChange(opt?.value || '')}
                                  placeholder={translate('form.select_placeholder')}
                                  isClearable={!(isCompleted || data?.has_evaluation_started)}
                                  isDisabled={isCompleted || data?.has_evaluation_started}
                                />
                              </div>
                            }
                          />
                        </div>
                  </>
                  </Field>
                </div>
              }
              <div className="form-group col-6">
                <Field label={getLabel('field.budget', 'Budget', fieldCustomNames?.['field.budget'])} error={errors?.budget_setting_hash_id?.message}>
                <>
                      <Controller
                        name={'budget_setting_hash_id'}
                        control={control}
                        render={({ field: f }) =>
                          <div style={{ display: 'contents' }} data-testid="budget-setting-hash-id-select">
                            <Select
                              {...f}
                              error={!!errors?.budget_setting_hash_id}
                              options={budgetOptions}
                              value={budgetOptions.find((o) => o.value === watchBudgetSettingHashId) || null}
                              onChange={(opt) => f.onChange(opt?.value)}
                              placeholder={translate('form.select_placeholder')}
                              isClearable
                            />
                          </div>
                        }
                      />
                </>
                </Field>
              </div>
              <div className="form-group col-6">
                <Field label={getLabel('field.group', 'Group', fieldCustomNames?.['field.group'])} error={errors?.group_hash_id?.message}>
                <>
                      <Controller
                        name={'group_hash_id'}
                        control={control}
                        render={({ field: f }) =>
                          <div style={{ display: 'contents' }} data-testid="group-hash-id-select">
                            <Select
                              {...f}
                              error={!!errors?.group_hash_id}
                              options={groupOptions}
                              value={groupOptions.find((o) => o.value === watchGroupHashId) || null}
                              onChange={(opt) => f.onChange(opt?.value)}
                              placeholder={translate('form.select_placeholder')}
                              isClearable={!isCompleted}
                              isDisabled={isCompleted}
                            />
                          </div>
                        }
                      />
                </>
                </Field>
              </div>
              <div className="form-group col-6">
                  <Field label={getLabel('field.pay_factor', 'Pay Factor', fieldCustomNames?.['field.pay_factor'], true)} error={errors?.pay_factor_source?.message || errors?.pay_factor?.message}>
                    <div className={`d-flex ${watchPayFactorSource === 'custom' ? 'select-with-input' : ''}`}>
                      <NativeSelect className={watchPayFactorSource === 'custom' ? 'max-50' : ''} error={!!errors?.pay_factor_source} {...register('pay_factor_source')} data-testid="pay-factor-source-select">
                        <option value="calculation_setting">{translate('field.auto', 'Berechnungseinstellung')}</option>
                        <option value="individual_pay_factor">{translate('field.individual_pay_factor', 'Individueller Entgeltfaktor')}</option>
                        <option value="custom">{translate('field.manual', 'Manuell')}</option>
                      </NativeSelect>
                      {
                        watchPayFactorSource === 'custom' && <Input type="text" error={!!errors?.pay_factor} {...register('pay_factor')} maxLength={11} data-testid="pay-factor-input" />
                      }
                    </div>
                    {
                      watchPayFactorSource === 'calculation_setting' && (
                        <div className={`form-text ws-normal ${!effectivePayFactorLoading && effectivePayFactorResolution && !effectivePayFactorResolution.available ? 'text-danger' : 'text-muted'}`}>
                          {effectivePayFactorLoading ? (
                            <small>{translate('balanced_scorecard.pay_factor_resolving', 'Entgeltfaktor wird ermittelt…')}</small>
                          ) : effectivePayFactorResolution?.available ? (
                            <>
                              <small className="d-block">
                                {translate('balanced_scorecard.pay_factor_effective_value', 'Verwendeter Wert')}: <strong>{removeTrailingZeros(String(effectivePayFactorResolution.value))}</strong>
                              </small>
                              <small className="d-block">{calculationSettingPayFactorSourceText}</small>
                            </>
                          ) : (
                            <small>{translate('balanced_scorecard.pay_factor_resolution_failed', 'Für diese Berechnungseinstellung konnte kein Entgeltfaktor ermittelt werden.')}</small>
                          )}
                        </div>
                      )
                    }
                  </Field>
                </div>
              <div className="form-group col-6 align-self-start">
                <Field label={getLabel('field.fixed_payout', 'Fixed Payout', fieldCustomNames?.['field.fixed_payout'], true)} htmlFor={`fixed-payout-${data?.hash_id}`} error={errors?.fixed_payout?.message}>
                  <InputAffix prefix="€" error={!!errors?.fixed_payout}>
                    <Input id={`fixed-payout-${data?.hash_id}`} type="text" error={!!errors?.fixed_payout} {...register('fixed_payout')} data-testid="fixed-payout-input" />
                  </InputAffix>
                </Field>
              </div>
              {
                (watchIndex > 0 || (data?.distribution_percentage && (data?.distribution_percentage !== '100' || lkWeightTotal !== 100))) && <div className="form-group col-6 align-self-start">
                  <Field label={getLabel('field.weighting', 'Weighted', fieldCustomNames?.['field.weighting'], true)} htmlFor={`distribution-percentage-${data?.hash_id}`} error={errors?.distribution_percentage?.message}>
                    <InputAffix suffix="%" error={!!errors?.distribution_percentage}>
                      <Input id={`distribution-percentage-${data?.hash_id}`} type="text" error={!!errors?.distribution_percentage} {...register('distribution_percentage')} maxLength={6} data-testid="distribution-percentage-input" />
                    </InputAffix>
                  </Field>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </form>
    <Modal
      open={confirmModalOpen}
      title={editableTranslate('employee_management.downstream_change_title', 'Bestätigung erforderlich')}
      cancelLabel={editableTranslate('button.cancel')}
      okLabel={editableTranslate('button.proceed')}
      onClose={handleAbortChange}
      onConfirm={handleConfirmChange}
      testIdPrefix="downstream-change-"
    >
      {editableTranslate(
        'employee_management.downstream_change_message',
        'Sie wollen eine abgeschlossene Leistungskarte ändern, was sich auf nachgelagerte Prozesse und die Berechnungen auswirken kann. Möchten Sie wirklich fortfahren?'
      )}
    </Modal>
  </div>
}

function PayFactorInput({ activePayfactorValues, watchPayGrade, watchPayGradeLevel, control, errors, setValue, missingPayfactorValues = false, missingPayfactorTooltipId = '', missingPayfactorTooltip = '' }) {
  const { translate } = useTranslation();

  const previousGradeRef = useRef(watchPayGrade);

  const normalizedRowsForGrade = useMemo(() => {
    return (activePayfactorValues || []).filter((row) => row.grade === watchPayGrade);
  }, [activePayfactorValues, watchPayGrade]);

  const normalizedGradeOptions = useMemo(() => {
    const map = new Map();

    (activePayfactorValues || []).forEach((row) => {
      if (!row?.grade || map.has(row.grade)) return;

      map.set(row.grade, {
        value: row.grade,
        label: row.grade_label || row.grade,
      });
    });

    return Array.from(map.values());
  }, [activePayfactorValues]);

  const normalizedLevelOptions = useMemo(() => {
    const map = new Map();

    normalizedRowsForGrade.forEach((row) => {
      if (!row?.level || map.has(row.level)) return;

      map.set(row.level, {
        value: row.level,
        label: row.level_label || row.level,
      });
    });

    return Array.from(map.values());
  }, [normalizedRowsForGrade]);

  const hasNormalizedLevelOptions = normalizedLevelOptions.length > 0;

  useEffect(() => {
    if (previousGradeRef.current !== watchPayGrade && normalizedRowsForGrade.length > 0 && !hasNormalizedLevelOptions && watchPayGradeLevel) {
      setValue('pay_grade_level', '');
    }

    previousGradeRef.current = watchPayGrade;
  }, [watchPayGrade, watchPayGradeLevel, normalizedRowsForGrade, hasNormalizedLevelOptions, setValue]);

  const gradeOptions = useMemo(() => {
    const arr = [];

    let curValueExist = false;

    normalizedGradeOptions.forEach((grade) => {
      arr.push(grade);
      if (grade.value === watchPayGrade) curValueExist = true;
    });

    if (watchPayGrade && !curValueExist) {
      arr.push({ value: watchPayGrade, label: watchPayGrade });
    }

    return arr;
  }, [normalizedGradeOptions, watchPayGrade]);

  const gradeLevelOptions = useMemo(() => {
    const arr = [];

    let curValueExist = false;

    normalizedLevelOptions.forEach((level) => {
      arr.push(level);
      if (level.value === watchPayGradeLevel) curValueExist = true;
    });

    if (watchPayGradeLevel && !curValueExist) {
      arr.push({ value: watchPayGradeLevel, label: watchPayGradeLevel });
    }

    return arr;
  }, [normalizedLevelOptions, watchPayGradeLevel]);

  return <>
    <div className="d-flex align-items-center">
      <Controller
        name={'pay_grade'}
        control={control}
        render={({ field: f }) => <div style={{ display: 'contents' }} data-testid="pay-grade-select">
          <Select {...f} options={gradeOptions} className="w-100" error={!!errors?.pay_grade} onChange={opt => f.onChange(opt?.value)} value={gradeOptions.find(o => o.value === watchPayGrade) || null} isClearable placeholder={translate('form.select_placeholder')} />
        </div>}
      />
      {(hasNormalizedLevelOptions || !!watchPayGradeLevel) && <>
        <span className="mx-2">/</span>
        <Controller
          name={'pay_grade_level'}
          control={control}
          render={({ field: f }) => <div style={{ display: 'contents' }} data-testid="pay-grade-level-select">
            <Select {...f} options={gradeLevelOptions} className="w-100" error={!!errors?.pay_grade_level} onChange={opt => f.onChange(opt?.value)} value={gradeLevelOptions.find(o => o.value === watchPayGradeLevel) || null} isClearable placeholder={translate('form.select_placeholder')} />
          </div>}
        />
      </>}
      {
        missingPayfactorValues && <>
          <i className="bi bi-exclamation-triangle-fill text-warning cursor-pointer ml-2" data-tooltip-id={missingPayfactorTooltipId}></i>
          <ReactTooltip id={missingPayfactorTooltipId} place="bottom" clickable>
            {missingPayfactorTooltip}
          </ReactTooltip>
        </>
      }
    </div>
    {
      (errors['pay_grade'] || errors['pay_grade_level']) && <div className="d-block invalid-feedback">
        {errors['pay_grade'] && errors['pay_grade'].message}
        {(errors['pay_grade'] && errors['pay_grade_level']) && ' / '}
        {errors['pay_grade_level'] && errors['pay_grade_level'].message}
      </div>}
  </>;
}

function DepartmentInput({ watchDepartment, control, errors }) {
  const { translate } = useTranslation();

  const [values, setValues] = useState([]);

  useEffect(() => {
    fetchValues();
  }, []);

  const options = useMemo(() => {
    const arr = [];

    let curValueExist = false;

    if (values.length > 0) {
      values.forEach(val => {
        arr.push({ value: val, label: val });

        if (val === watchDepartment) curValueExist = true;
      });
    }

    if (watchDepartment && !curValueExist) {
      arr.push({ value: watchDepartment, label: watchDepartment });
    }

    return arr;
  }, [values, watchDepartment]);

  const fetchValues = async () => {
    try {
      const { data, status } = await api.get(`/settings/fields/department/values`);
      if (status === 200) {
        setValues(data.data);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  return <>
    <Controller
      name={'department'}
      control={control}
      render={({ field: f }) => <div style={{ display: 'contents' }} data-testid="department-select">
        <Select as={Creatable} {...f} options={options} error={!!errors?.department} onChange={opt => f.onChange(opt?.value)} value={options.find(o => o.value === watchDepartment) || null} isClearable placeholder={translate('form.select_placeholder')} noOptionsMessage={() => translate('form.no_options_createable')} formatCreateLabel={val => translate('form.createable_label') + ` "${val}"`} />
      </div>}
    />
    {errors?.department && (
      <div className="peb-field__error">
        {errors?.department.message}
      </div>
    )}
  </>;
}

export default function EmployeeRow({
  employee,
  tableFields = null,
  fixedFieldsView,
  systemFieldsView,
  freeFieldsView,
  balancedScorecardFieldsView,
  calculationRelatedFieldsView,
  conditionalFieldsView,
  fixedFieldsData,
  systemFieldsData,
  freeFieldsData,
  calculationRelatedFieldsData,
  calculationRelatedFields,
  conditionalFieldsData,
  fetchEmployees,
  selectedEmployee,
  setSelectedEmployee,
  softwareSettings,
  interviews,
  handleCancelAddEmployee,
  isInternalTenant,
  sortCols,
  setEmployeeDirty,
  setEmployeeLkDirty,
  resizedCount = null,
  noDetails = false,
  getLabel,
  userRoles,
  activeTab
}) {
  const editorModules = {
    toolbar: [
      ['bold', 'italic', 'underline', { 'list': 'bullet' }, { 'list': 'ordered' }]
    ]
  };

  const editorFormats = ['bold', 'italic', 'underline', 'bullet', 'list'];

  const lang = useSelector(getLang);
  const currentModule = useSelector(getModule);

  const fieldCustomNames = useSelector(getAllCustomNames);

  const { translate, editableTranslate } = useTranslation();

  const permissions = useSelector(getPermissions);
  const canManageAssignments = canManageEmployeeAssignments(userRoles, 'lob');

  const payfactorCatalogValues = useSelector(selectGlobalPayfactorValues);

  const [showEmployeeDataEditForm, setShowEmployeeDataEditForm] = useState(false);

  const [employeeBalancedScorecards, setEmployeeBalancedScorecards] = useState([]);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [balancedScorecardOpened, setBalancedScorecardOpened] = useState(false);

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [loadingDeactivate, setLoadingDeactivate] = useState(false);
  const [finalAssignmentCount, setFinalAssignmentCount] = useState(null);

  const [showDeleteLkAlert, setShowDeleteLkAlert] = useState(false);
  const [showSecondLkAlert, setShowSecondLkAlert] = useState(false);

  const employmentDateBoth = useMemo(() => {
    if (fixedFieldsData) {
      const filtered = fixedFieldsData.filter(f => ['employment_entry_date', 'employment_exit_date'].includes(f.slug));
      if (filtered.length === 2) return true;
    }
    return false;
  }, [fixedFieldsData]);

  const defaultInfos = useMemo(() => {
    const temp = {};
    if (!employee && !employee?.employeeInformation) return temp;

    employee.employeeInformation.data.forEach(field => {
      temp[field.key] = field.value;
    });
    return temp;
  }, [employee]);

  const lkWeightTotal = useMemo(() => {
    let total = 0;
    employeeBalancedScorecards.forEach(lk => {
      if (lk) {
        total += +formatNumberTo(lk.distribution_percentage, '.');
      }
    });
    return total;
  }, [employeeBalancedScorecards]);

  const hasAddLkForm = useMemo(() => {
    for (const lk of employeeBalancedScorecards) {
      if (lk === null) return true;
    }
    return false;
  }, [employeeBalancedScorecards]);

  const mergedCalculationRelatedFieldsData = useMemo(() => {
    if (!calculationRelatedFieldsData || !systemFieldsData || !conditionalFieldsData) {
      return [];
    }

    // Merge the arrays
    const mergedData = [
      ...calculationRelatedFieldsData.filter(f => f.key !== 'pay_grade_level'),
      ...systemFieldsData,
      ...conditionalFieldsData.filter(f => f?.source !== 'state_employee_config'),
    ].filter(f => !REFERENCE_FIELD_KEYS.includes(f.key));

    // Separate the "Excluded from Payout" field
    const excludedIndex = mergedData.findIndex(field => field.name === "Excluded from Payout");

    let excludedField = null;
    if (excludedIndex !== -1) {
      excludedField = mergedData.splice(excludedIndex, 1)[0]; // Remove it from array
    }

    // Add it back at the end if it exists
    if (excludedField) {
      mergedData.push(excludedField);
    }

    return mergedData;
  }, [calculationRelatedFieldsData, systemFieldsData, conditionalFieldsData]);

  const schema = useMemo(() => {
    const formatFields = {};
    if (calculationRelatedFields) {
      calculationRelatedFields.filter(f => f.decimal !== null).forEach(f => {
        formatFields[f.slug] = f;
      });
    }

    const freeFieldsObj = {};
    if (freeFieldsData) {
      for (const f of freeFieldsData) {
        freeFieldsObj[f?.name] = yup.string().notRequired().max(255).label(f?.custom_name || translate(f?.translation_key, f?.name));
      }
    }

    return yup.object({
      last_name: yup.string().required().label(fieldCustomNames?.['field.last_name'] || translate('field.last_name')),
      first_name: yup.string().required().label(fieldCustomNames?.['field.first_name'] || translate('field.first_name')),
      employee_number: yup.string().required().label(fieldCustomNames?.['field.employee_number'] || translate('field.employee_number')),
      individual_pay_factor: yup.string().transform(cv => cv ? cv : '').notRequired()
        .test('positive-number', translate('form.positive_number_required', 'Bitte einen positiven Wert eingeben.'), value => {
          if (!value) return true;
          return Number(String(value).replace(',', '.')) > 0;
        })
        .test('is-decimal', translate(`form.valid_format_decimal_${formatFields['individual_pay_factor']?.decimal || 0}`), value => {
          if (!value) return true;
          return fieldDecimalCheck(value, formatFields['individual_pay_factor']);
        })
        .label(fieldCustomNames?.['field.individual_pay_factor'] || translate('field.individual_pay_factor', 'individueller Entgeltfaktor')),
      working_hours: softwareSettings && softwareSettings.working_hours_type === 'hours'
        ? yup
          .number()
          .nullable()
          .transform((value, originalValue) => originalValue === '' ? null : value)
          .integer()
          .min(0)
          .max(50)
          .label(fieldCustomNames?.['field.working_hours'] || translate('field.working_hours'))
        : yup
          .string()
          .transform(cv => cv ? cv : '')
          .notRequired()
          .test('is-decimal', translate(`form.valid_format_decimal_${formatFields['working_hours']?.decimal || 0}`), value => {
            if (!value) return true;
            return fieldDecimalCheck(value, formatFields['working_hours'])
          })
          .test('number-range', translate('form.valid_range') + ' 5-100', value => {
            if (!value) return true;
            const temp = Number(formatNumberTo(value, '.'));
            return temp >= 5 && temp <= 100;
          })
          .label(fieldCustomNames?.['field.working_hours'] || translate('field.working_hours')),
      email: yup.string().email().notRequired().label(fieldCustomNames?.['field.email'] || translate('field.email')),
      department: yup.string().notRequired().label(fieldCustomNames?.['field.department'] || translate('field.department')),
      gender: yup.string().nullable().max(255).label(fieldCustomNames?.['field.gender'] || translate('field.gender', 'Gender')),
      employment_entry_date: yup.string().notRequired().label(fieldCustomNames?.['field.employment_entry_date'] || translate('field.employment_entry_date'))
        .test('valid-date', 'Invalid Date', val => {
          if (!val || val.trim() === '') return true;

          return dayjs(val, 'DD.MM.YYYY').isValid();
        }),
      employment_exit_date: yup.string().notRequired().label(fieldCustomNames?.['field.employment_exit_date'] || translate('field.employment_exit_date')).test('valid-exit-date', translate('employee_management.exit_date_cannot_before_entry_date', 'Das Austrittsdatum darf nicht vor dem Eintrittsdatum liegen.'), (val, ctx) => {
        if (!val || val.trim() === '') return true;
        else {
          const ex = dayjs(val, 'DD.MM.YYYY');
          if (ex.isValid() && ex.isSameOrAfter(dayjs(ctx.parent.employment_entry_date, 'DD.MM.YYYY'))) return true;
        }
      }),
      annual_compensation: yup.string().notRequired()
        .transform(cv => cv ? cv : '')
        .test('number-range', translate('form.valid_range') + ' 0-999999,99', value => {
          if (!value) return true;
          const temp = Number(formatNumberTo(value, '.'));
          return temp >= 0 && temp <= 999999.99;
        })
        .test('is-decimal', translate('form.valid_format_decimal_2'), value => !value ? true : (value.toString()).match(/^\d*(,{1}(\d{1,2}))?$/))
        .label(fieldCustomNames?.['field.annual_compensation'] || translate('field.annual_compensation')),
      missing_months: yup.string().transform(cv => cv ? cv : '').notRequired()
        .test('is-decimal', translate('form.valid_format_decimal_3'), value => {
          if (!value) return true;
          return fieldDecimalCheck(value, { decimal: 3 })
        })
        .test('number-range', translate('form.valid_range') + ' 0-36', value => {
          if (!value) return true;
          const temp = Number(formatNumberTo(value, '.'));
          return temp >= 0 && temp <= 36;
        })
        .label(fieldCustomNames?.['field.missing_months'] || translate('field.missing_months')),
      working_days_per_week: yup.string().transform(cv => cv ? cv : '').notRequired()
        .test('is-decimal', translate('form.valid_format_decimal_0'), value => {
          if (!value) return true;
          return fieldDecimalCheck(value, { decimal: 0 })
        })
        .test('number-range', translate('form.valid_range') + ' 1-7', value => {
          if (!value) return true;
          const temp = Number(formatNumberTo(value, '.'));
          return temp >= 1 && temp <= 7;
        })
        .label(fieldCustomNames?.['field.working_days_per_week'] || translate('field.working_days_per_week')),
      missing_days: yup.string().transform(cv => cv ? cv : '').notRequired()
        .test('is-decimal', translate('form.valid_format_decimal_0'), value => {
          if (!value) return true;
          return fieldDecimalCheck(value, { decimal: 0 })
        })
        .test('number-range', translate('form.valid_range') + ' 0-366', value => {
          if (!value) return true;
          const temp = Number(formatNumberTo(value, '.'));
          return temp >= 0 && temp <= 366;
        })
        .label(fieldCustomNames?.['field.missing_days'] || translate('field.missing_days')),
      exclude_from_payout_reason: yup.string().notRequired().when('exclude_from_payout', {
        is: true,
        then: (schema) => schema.required(translate('field.error.exclude_from_payout_reason')).min(10).max(50).label(translate('field.exclude_from_payout_reason'))
      }),
      manual_average_value: yup.string().when('trend', {
        is: 'yes_manual_value',
        then: (schema) => schema.required()
          .test('number-range', translate('form.valid_range') + ' 0-999,99', value => {
            const temp = Number(formatNumberTo(value, '.'));
            return temp >= 0 && temp <= 999.99;
          })
          .test('is-decimal', translate('form.valid_format_decimal_2'), value => !value ? true : (value.toString()).match(/^\d*(,{1}(\d{1,2}))?$/)),
        otherwise: (schema) => schema.nullable(true)
      }),
      trend: yup.string().notRequired().label(fieldCustomNames?.['field.trend'] || translate('field.trend')),
      pay_grade: yup.string().notRequired().label(fieldCustomNames?.['field.pay_grade'] || translate('field.pay_grade')),
      pay_grade_level: yup.string().notRequired().label(fieldCustomNames?.['field.pay_grade_level'] || translate('field.pay_grade_level')),
      is_example: yup.boolean().notRequired(),
      individual_ceiling: yup.string().notRequired()
        .transform(cv => cv ? cv : '')
        .test('number-range', translate('form.valid_range') + ' 0-999999,99', value => {
          if (!value) return true;
          const temp = Number(formatNumberTo(value, '.'));
          return temp >= 0 && temp <= 999999.99;
        })
        .test('is-decimal', translate('form.valid_format_decimal_2'), value => !value ? true : (value.toString()).match(/^\d*(,{1}(\d{1,2}))?$/))
        .label(fieldCustomNames?.['field.individual_ceiling'] || translate('field.individual_ceiling')),
      individual_average_rating: yup.string().notRequired()
        .transform(cv => cv ? cv : '')
        .test('number-range', translate('form.valid_range') + ' 0-999999,999', value => {
          if (!value) return true;
          const temp = Number(formatNumberTo(value, '.'));
          return temp >= 0 && temp <= 999999.999;
        })
        .test('is-decimal', translate('form.valid_format_decimal_3'), value => {
          if (!value) return true;
          return fieldDecimalCheck(value, { decimal: 3 });
        })
        .label(fieldCustomNames?.['field.individual_average_rating'] || translate('field.individual_average_rating')),
      employee_information: yup.object().shape({
        ...freeFieldsObj
      }).notRequired()
    });
  }, [calculationRelatedFields, freeFieldsData, fieldCustomNames]);

  const { register, handleSubmit, getValues, setValue, control, reset, watch, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(schema)
  });

  const watchExcludeFromPayout = watch('exclude_from_payout');
  const watchTrend = watch('trend');
  const watchIndividualPayFactor = watch('individual_pay_factor');
  const watchPayGrade = watch('pay_grade');
  const watchPayGradeLevel = watch('pay_grade_level');
  const watchDepartment = watch('department');
  const watchGender = watch('gender');
  const [revealedOptionalFields, setRevealedOptionalFields] = useState([]);

  const hasStoredValue = (key) => !!employee && !empty(employee[key]);
  const isOptionalFieldShown = (key) => hasStoredValue(key) || revealedOptionalFields.includes(key);

  const toggleOptionalField = (key, checked) => {
    if (checked) {
      setRevealedOptionalFields(prev => [...prev, key]);
    } else {
      setRevealedOptionalFields(prev => prev.filter(k => k !== key));
      setValue(key, '', { shouldDirty: true, shouldValidate: true });
    }
  };

  const optionalFieldToggles = mergedCalculationRelatedFieldsData
    .filter(field => OPTIONAL_CALCULATION_FIELDS.includes(field.key) && !hasStoredValue(field.key))
    .map(field => (
      <Checkbox
        key={field.key}
        id={`optional-field-${field.key}`}
        className="mr-4 mb-2"
        label={getLabel(field.translation_key, field.name, field?.custom_name)}
        checked={revealedOptionalFields.includes(field.key)}
        onChange={e => toggleOptionalField(field.key, e.target.checked)}
        data-testid={`optional-field-toggle-${field.key}`}
      />
    ));
  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    setEmployeeDirty(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (employee) {
      setEmployeeBalancedScorecards(employee.balanced_scorecards.data);
    } else {
      setShowEmployeeDataEditForm(true);
    }
    setBalancedScorecardOpened(false);
  }, [employee]);

  useEffect(() => {
    setEmployeeLkDirty(false);
  }, [balancedScorecardOpened]);

  useEffect(() => {
    if (error) {
      const timeoutId = setTimeout(() => {
        if (employee) scrollInto('.selected-employee-row', false, -100);
        else scrollInto('.employee-new', false, -100);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [error]);

  const clearFormAndCallCancel = () => {
    reset();
    setRevealedOptionalFields([]);

    if (!employee) {
      setSelectedEmployee(null);
      handleCancelAddEmployee();
    }

    setShowEmployeeDataEditForm(false);
  };

  const handleClick = () => {
    if (selectedEmployee === employee.hash_id) {
      setSelectedEmployee(null);
    } else {
      setSelectedEmployee(employee.hash_id);
    }
    setBalancedScorecardOpened(false);
    clearFormAndCallCancel();
  };

  const handleEmployeeDataAction = (e, employee) => {
    e?.currentTarget?.blur();
    e?.preventDefault();

    reset({
      last_name: employee?.last_name,
      first_name: employee?.first_name,
      email: employee?.email,
      department: employee?.department,
      gender: employee?.gender || null,
      employee_number: employee?.employee_number,
      individual_pay_factor: removeTrailingZeros(employee?.individual_pay_factor),
      working_hours: removeTrailingZeros(employee?.working_hours),
      working_days_per_week: removeTrailingZeros(employee?.working_days_per_week),
      employment_entry_date: employee?.employment_entry_date,
      employment_exit_date: employee?.employment_exit_date,
      employee_information: { ...defaultInfos },
      annual_compensation: removeTrailingZeros(employee?.annual_compensation),
      missing_months: removeTrailingZeros(employee?.missing_months),
      missing_days: removeTrailingZeros(employee?.missing_days),
      pay_grade: employee?.pay_grade,
      pay_grade_level: employee?.pay_grade_level,
      exclude_from_payout: employee?.exclude_from_payout,
      exclude_from_payout_reason: employee?.exclude_from_payout_reason,
      trend: employee?.trend || 'no',
      manual_average_value: employee?.manual_average_value,
      is_example: employee?.is_example,
      individual_ceiling: removeTrailingZeros(employee?.individual_ceiling),
      individual_average_rating: removeTrailingZeros(employee?.individual_average_rating),
    });

    setRevealedOptionalFields([]);
    setShowEmployeeDataEditForm(!showEmployeeDataEditForm);
  };

  const payloadFields = { fixedFieldsData, calculationRelatedFieldsData, conditionalFieldsData, systemFieldsData, freeFieldsData };

  const formatErrorMessage = (err) => {
    let formatted = errorMessage(err, 'save employee');
    if (err?.response?.data) {
      const errData = err?.response?.data;
      if (errData?.data?.existing_employee_number) {
        const conflict = errData.data.existing_employee_number;
        const hasAssignedModule = Array.isArray(conflict.assigned_modules)
          ? conflict.assigned_modules.length > 0
          : Boolean(conflict.module);
        if (!hasAssignedModule) {
          formatted = translate('message.error.employee_number_already_exists_inactive');
        } else {
          formatted = translate('message.error.employee_number_already_exists');
        }
        formatted = formatted.replace('$X', '<b>' + errData?.data?.existing_employee_number?.name + '</b>');
      } else if (errData?.data?.existing_email_and_employee_number) {
        const obj = errData?.data?.existing_email_and_employee_number;
        formatted = translate('message.error.email_and_employee_number_belong_to_different_employees')
          .replace('$X', '<b>' + obj?.email?.name + '</b>')
          .replace('$Y', '<b>' + obj?.employee_number?.name + '</b>');
      } else if (errData?.data?.existing_email) {
        formatted = translate('message.error.email_already_exists')
          .replace('$X', '<b>' + errData?.data?.existing_email?.name + '</b>');
      }
    }
    return formatted;
  };

  const onSubmit = async (data) => {
    if (!employee) return handleCreateNewEmployee(data);

    setError(null);
    setLoading(true);

    const formData = buildEmployeePayload(data, payloadFields, { dropReferenceKeys: true });

    try {
      const { status } = await api.put(`/employees/${employee.hash_id}`, formData);
      if (status === 200) {
        fetchEmployees(false, false);

        setLoading(false);
        setShowEmployeeDataEditForm(false);
        setRevealedOptionalFields([]);
        reset();

        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setLoading(false);
      setShowEmployeeDataEditForm(true);
      setError(formatErrorMessage(err));
      Sentry.captureException(err);
    }
  };

  const handleCreateNewEmployee = async (data) => {
    setError(null);
    setLoading(true);

    const formData = buildEmployeePayload(data, payloadFields, { dropReferenceKeys: true });

    try {
      const { status } = await api.post(`/employees/new`, formData);
      if (status === 200) {
        handleCancelAddEmployee();

        setLoading(false);

        toast.success(translate('form.success.saved'));

        await fetchEmployees(false, false);
      }
    } catch (err) {
      setLoading(false);
      setError(formatErrorMessage(err));
      Sentry.captureException(err);
    }
  };

  const handleAddBalancedScorecard = () => {
    if (!balancedScorecardOpened) setEmployeeBalancedScorecards([...employeeBalancedScorecards, null]);
  };

  const handleCancelAddBalancedScorecard = () => {
    setEmployeeBalancedScorecards(employeeBalancedScorecards.filter(bs => bs));
  };

  const openDeactivateModal = () => {
    setError(null);
    setFinalAssignmentCount(null);
    setShowDeactivateModal(true);
  };

  const closeDeactivateModal = () => {
    if (loadingDeactivate) return;
    setFinalAssignmentCount(null);
    setShowDeactivateModal(false);
  };

  const handleEmployeeDeactivate = async (confirmFinalAssignment = false) => {
    if (!employee) return;

    setLoadingDeactivate(true);
    setError(null);

    try {
      const payload = confirmFinalAssignment
        ? withFinalAssignmentConfirmation({})
        : {};
      const { status } = await api.post(`/employees/${employee?.hash_id}/deactivate`, payload);
      if (status === 200) {
        toast.success(translate('employee_management.deactivate_success', 'Die Person wurde in dieser Periode deaktiviert.'));

        setFinalAssignmentCount(null);
        setShowDeactivateModal(false);

        await fetchEmployees(false, false);
      }
    } catch (err) {
      const confirmation = finalAssignmentConfirmationDetails(err);
      if (confirmation && !confirmFinalAssignment) {
        setFinalAssignmentCount(confirmation.count || 1);
      } else {
        if (confirmFinalAssignment) {
          setFinalAssignmentCount(null);
          setShowDeactivateModal(false);
        }
        setError(errorMessage(err, 'deactivate employee in period'));
        Sentry.captureException(err);
      }
    }

    setLoadingDeactivate(false);
  };

  return <>
    <tr className="tr-separator"></tr>
    {
      employee && <tr className={`table-row ${selectedEmployee === employee.hash_id ? 'selected-employee-row' : ''} ${noDetails ? 'no-select' : ''}`} onClick={noDetails ? null : handleClick} data-testid={`employee-row-${employee.hash_id}`}>
        {
          !noDetails && <td className="text-center v-middle w-1px"><i className={`open-row-icon bi bi-chevron-${selectedEmployee === employee.hash_id ? 'up' : 'down'}`}></i></td>
        }
        {
          (tableFields ? tableFields.filter(f => f.displayed) :
            [...fixedFieldsView, ...calculationRelatedFieldsView, ...conditionalFieldsView, ...systemFieldsView, ...freeFieldsView, ...balancedScorecardFieldsView]).map((field, i) => {
              return <EmployeeTableColumn key={i} field={field} employee={employee} sortCols={sortCols} forEmployeeManagement={true} resizedCount={resizedCount} />
            })
        }
      </tr>
    }
    {
      (!employee || selectedEmployee === employee.hash_id) && <tr className={`employee-detail-row ${!employee ? 'employee-new' : ''}`} data-testid="employee-detail">
        <td colSpan={16}>
          <div className="employee-detail-div">
            {
              error && <div className="pb-2">
                <ErrorAlert error={error} isHtml={true} onClose={() => setError(null)} />
              </div>
            }

            <div className="row flex-nowrap">
              <div className={`col-auto employee-data-card-col`}>
                <div className="h-100">
                  <form onSubmit={handleSubmit(onSubmit)} className="employee-data-card" data-testid="employee-form">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h4 className="mb-0">{editableTranslate('employee.employee_data', 'Stammdaten')}</h4>

                      <div>
                        {
                          (employee && !showEmployeeDataEditForm) ? <>
                            <Button onClick={openDeactivateModal} variant="secondary" className={`mr-2 ${hasPermissionTo('employee_management.manage_employees.*', permissions) && canManageAssignments ? '' : 'invisible'}`} data-testid="deactivate-button"><i className="bi bi-person-x text-normal"></i> {editableTranslate('employee_management.deactivate', 'Deaktivieren')}</Button>
                            <Button onClick={(e) => handleEmployeeDataAction(e, employee)} variant="secondary" className={hasPermissionTo('employee_management.manage_employees.*', permissions) ? '' : 'invisible'} data-testid="edit-button"><i className="bi bi-pencil-square text-normal"></i> {editableTranslate('global.edit')}</Button>
                          </> : <>
                            <Button variant="secondary" onClick={() => clearFormAndCallCancel()} data-testid="cancel-button" className="mr-2">
                              {editableTranslate("button.cancel")}
                            </Button>
                            <Button type="submit" variant="primary" loading={loading} data-testid="save-button">{editableTranslate("button.save")}</Button>
                          </>
                        }
                      </div>
                    </div>
                    <div>
                      <div className="employee-data-card-div" key={'basic_data'}>
                        <div className="card-setting-header d-flex justify-content-between align-items-center">
                          <span>{editableTranslate('employee.basic_data', 'Personaldaten')}</span>
                          <div>
                            {
                              employee && employee.is_example && <i className="bi bi-flag" data-tooltip-id="example-employee-tooltip"></i>
                            }
                            {
                              showEmployeeDataEditForm && <span className="ml-3"><span className="text-default">*</span> {editableTranslate('user_import.required')}</span>
                            }
                          </div>
                        </div>
                        <hr className="mt-2" />
                        <div className="card-setting-body mt-3">
                          {
                            (employee && !showEmployeeDataEditForm) ? <div className="row">
                              {
                                [...fixedFieldsData].map((field, i) => {
                                  const translatedName = field?.custom_name || editableTranslate(field.translation_key, field.name);
                                  let val = '';

                                  if (employee.hasOwnProperty(field.key) && employee[field.key] !== undefined && employee[field.key] !== null) {
                                    val = employee[field.key];
                                  } else if (employee.information.hasOwnProperty(field.name) && employee.information[field.name] !== undefined && employee.information[field.name] !== null) {
                                    val = employee.information[field.name];
                                  }

                                  if (val === true) val = translate('form.yes', 'Ja');
                                  else if (val === false) val = translate('form.no', 'nein');
                                  else if (field.key === 'trend') {
                                    val = translate('field.trend.' + val, val);
                                  } else if (field.key === 'gender') {
                                    val = translateGenderValue(val, translate);
                                  }
                                  // else if (field.key === 'email') {
                                  //   val = val.substr(0, 30) + (val.length > 30 ? '...' : '');
                                  // }

                                  if (empty(val)) val = '-';

                                  return <div key={i} className="col-6 col-xl2-4 d-flex justify-content-between p-0">
                                    <div className="col card-body-key">{translatedName}</div>
                                    <div className={`col card-body-value ${field.key === 'email' ? 'use-ellipsis' : ''}`} data-tooltip-id={field.key === 'email' ? `employee-email-${employee.hash_id}` : ''}>{val}</div>
                                    {
                                      (field.key === 'email' && val !== '-') && <ReactTooltip id={`employee-email-${employee.hash_id}`} place="bottom" content={val} />
                                    }
                                  </div>
                                })
                              }
                            </div> : <div className="row">
                              {
                                isInternalTenant && <>
                                  <div className="col-xl-12 col-lg-12">
                                    <div className="form-group">
                                      <div className="form-check">
                                        <input type="checkbox" className="form-check-input" id="is_example" {...register("is_example")} data-testid="is-example-checkbox" />
                                        <label className="form-check-label ml-2" htmlFor="is_example">
                                          {getLabel('field.is_example', 'Example Employee')}
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              }
                              {fixedFieldsData.map((field, i) => {
                                const translatedName = getLabel(field.translation_key, field.name, field?.custom_name);

                                if (employmentDateBoth) {
                                  if (field.key === 'employment_exit_date') return <Fragment key={i}></Fragment>;
                                  if (field.key === 'employment_entry_date') {
                                    return <div key={i} className="col-6 col-xl2-4">
                                      <div className="form-group">
                                        <label className="peb-field__label">{getLabel('field.employment_entry_exit_date')}</label>
                                        <div className="d-flex align-items-center">
                                          <DateInput error={!!errors?.employment_entry_date}
                                            {...register('employment_entry_date')} testId="employment-entry-date-input" />
                                          <div className="mx-2">-</div>
                                          <DateInput error={!!errors?.employment_exit_date}
                                            {...register('employment_exit_date')} testId="employment-exit-date-input" />
                                        </div>
                                        {(errors?.employment_entry_date || errors?.employment_exit_date) && (
                                          <div className="invalid-feedback d-block">
                                            {[errors?.employment_entry_date?.message || errors?.employment_exit_date?.message].filter(m => m).join(', ')}
                                          </div>
                                        )}
                                      </div>
                                    </div>;
                                  }
                                }

                                let label = (
                                  <>
                                    <label className="peb-field__label">
                                      {translatedName}
                                      <span
                                        className={`peb-field__req ${[
                                          "email",
                                          "employment_exit_date",
                                          "employment_entry_date",
                                          "department"
                                        ].includes(field.key) && "d-none"
                                          }`}
                                      >
                                        *
                                      </span>
                                    </label>
                                  </>
                                );
                                let input = (
                                  <Field error={errors[field.key]?.message}>
                                    {field.key.includes("date") ? (
                                      <DateInput
                                        error={!!errors[field.key]}
                                        {...register(field.key)}
                                        maxLength={255}
                                      />
                                    ) : (
                                      <Input
                                        type="text"
                                        error={!!errors[field.key]}
                                        autoComplete="on"
                                        {...register(field.key)}
                                        maxLength={255}
                                      />
                                    )}
                                  </Field>
                                );

                                if (field.key === "exclude_from_payout") {
                                  // checkbox input
                                  label = (
                                    <>
                                      <label className="peb-field__label">
                                        {getLabel('field.manual_exclusion', 'Manual Exclusion')}
                                      </label>
                                    </>
                                  );
                                  input = (
                                    <>
                                      <div>
                                        <div className="form-check">
                                          <input
                                            type="checkbox"
                                            className="form-check-input"
                                            data-testid={`employee-field-${field.key}-checkbox`}
                                            {...register(field.key)}
                                          />
                                          <label className="form-check-label ml-2">
                                            {translatedName}
                                          </label>
                                        </div>

                                        {watchExcludeFromPayout && (
                                          <>
                                            <div className={`form-group mt-2`}>
                                              <Field
                                                label={editableTranslate("field.exclude_from_payout_reason", "Begründung")}
                                                required
                                                error={errors["exclude_from_payout_reason"]?.message}
                                              >
                                                <Input
                                                  type="text"
                                                  error={!!errors["exclude_from_payout_reason"]}
                                                  {...register("exclude_from_payout_reason")}
                                                  maxLength={255} data-testid="exclude-from-payout-reason-input"
                                                />
                                              </Field>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </>
                                  );
                                } else if (field.key === "trend") {
                                  // radio input
                                  input = (
                                    <>
                                      <div>
                                        <div className="form-check">
                                          <input
                                            type="radio"
                                            className="form-check-input"
                                            value="no"
                                            data-testid="employee-field-trend-no"
                                            {...register(field.key)}
                                          />
                                          <label className="form-check-label ml-2">
                                            {editableTranslate("field.trend.no", "Nein")}
                                          </label>
                                        </div>
                                        <div className="form-check">
                                          <input
                                            type="radio"
                                            className="form-check-input"
                                            value="yes_calculated_from_budget"
                                            data-testid="employee-field-trend-yes-calculated-from-budget"
                                            {...register(field.key)}
                                          />
                                          <label className="form-check-label ml-2">
                                            {editableTranslate("field.trend.yes_calculated_from_budget", "Ja, aus dem Budget berechnet")}
                                          </label>
                                        </div>
                                        <div className="form-check">
                                          <input
                                            type="radio"
                                            className="form-check-input"
                                            value="yes_manual_value"
                                            data-testid="employee-field-trend-yes-manual-value"
                                            {...register(field.key)}
                                          />
                                          <label className="form-check-label ml-2">
                                            {editableTranslate("field.trend.yes_manual_value", "Ja, manuelle Eingabe")}
                                          </label>
                                        </div>
                                      </div>

                                      {watchTrend === "yes_manual_value" && (
                                        <>
                                          <div className={`form-group mt-2`}>
                                            <Field
                                              label={editableTranslate("field.trend.yes_manual_value.value", "Wert")}
                                              required
                                              error={errors["manual_average_value"]?.message}
                                            >
                                              <Input
                                                type="text"
                                                error={!!errors["manual_average_value"]}
                                                {...register("manual_average_value")}
                                                maxLength={24} data-testid="manual-average-value-input"
                                              />
                                            </Field>
                                          </div>
                                        </>
                                      )}
                                    </>
                                  );
                                } else if (field.key === "department") {
                                  // radio input
                                  input = <DepartmentInput watchDepartment={watchDepartment} control={control} errors={errors} />;
                                } else if (field.key === "gender") {
                                  input = <GenderSelect control={control} errors={errors} value={watchGender} acceptedValues={field.accepted_values} />;
                                }

                                return (
                                  <div key={i} className="col-6 col-xl2-4">
                                    <div className="form-group">
                                      {label}
                                      {input}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          }
                        </div>
                      </div>
                      <div className="employee-data-card-div" key={'calculation_related'}>
                        <div className="card-setting-header d-flex justify-content-between align-items-center">
                          <span>{editableTranslate('employee.calculation_related', 'Abrechnungsdaten')}</span>
                        </div>
                        <hr className="mt-2" />
                        <div className="card-setting-body mt-3">
                          {
                            (employee && !showEmployeeDataEditForm) ? <div className="row">
                              {
                                !employee.exclude_from_payout && mergedCalculationRelatedFieldsData.map((field, i) => {
                                  if (OPTIONAL_CALCULATION_FIELDS.includes(field.key) && !hasStoredValue(field.key)) return null;

                                  const translatedName = field?.custom_name || editableTranslate(field.translation_key, field.name);

                                  let label = translatedName;
                                  let val = '';

                                  if (employee.hasOwnProperty(field.key) && employee[field.key] !== undefined && employee[field.key] !== null) {
                                    val = employee[field.key];
                                  } else if (employee.information.hasOwnProperty(field.name) && employee.information[field.name] !== undefined && employee.information[field.name] !== null) {
                                    val = employee.information[field.name];
                                  }

                                  if (val === true) val = translate('form.yes', 'Ja');
                                  else if (val === false) val = translate('form.no', 'nein');
                                  else if (field.key === 'working_hours') {
                                    if (!empty(val)) {
                                      val = formatFixedDigits(val, field.decimal, field.fill_up) + (softwareSettings?.working_hours_type === 'hours' ? ' ' + translate('general_settings.working_hours_type.hours') : translate('general_settings.working_hours_type.percentage'));
                                    }
                                  } else if (['missing_months', 'missing_days', 'working_days_per_week'].includes(field.key)) {
                                    if (!empty(val)) val = formatFixedDigits(val, field.decimal, field.fill_up);
                                  } else if (field.key === 'individual_pay_factor') {
                                    if (!empty(val)) val = formatFixedDigits(val, field.decimal, field.fill_up);
                                  } else if (['annual_compensation', 'individual_ceiling'].includes(field.key)) {
                                    if (!empty(val)) val = '€ ' + formatMoney(val);
                                  } else if (['individual_average_rating'].includes(field.key)) {
                                    if (!empty(val)) val = formatFixedDigits(val, field.decimal, field.fill_up);
                                  } else if (field.key === 'pay_grade') {
                                    label = <>
                                      {fieldCustomNames?.['field.pay_grade'] || editableTranslate('field.pay_grade')}
                                      <span> / </span>
                                      {fieldCustomNames?.['field.pay_grade_level'] || editableTranslate('field.pay_grade_level')}
                                    </>;
                                    const displayGrade = employee['pay_grade'] || '-';
                                    const displayLevel = employee['pay_grade_level']
                                      ? employee['pay_grade_level'].replace('Stufe ', '')
                                      : '-';

                                    val = <span>{displayGrade} / {displayLevel}</span>;
                                  }

                                  if (empty(val)) val = '-';

                                  return <div key={i} className="col-6 col-xl2-4 d-flex justify-content-between p-0">
                                    <div className="col card-body-key">{label}</div>
                                    <div className="col card-body-value">{val}</div>
                                  </div>
                                })
                              }
                              {
                                employee.exclude_from_payout && <>
                                  <div className="col col-xl-4 d-flex justify-content-between p-0">
                                    <div className="col card-body-key">{fieldCustomNames?.['field.exclude_from_payout'] || editableTranslate('field.exclude_from_payout')}</div>
                                    <div className="col card-body-value">{translate('form.yes', 'Ja')}</div>
                                  </div>
                                  <div className="col col-xl-8 d-flex justify-content-between p-0">
                                    <div className="col card-body-key">{editableTranslate('field.exclude_from_payout_reason')}</div>
                                    <div className="col card-body-value">{employee.exclude_from_payout_reason || '-'}</div>
                                  </div>
                                </>
                              }
                            </div> : <div className="row">
                              {
                                mergedCalculationRelatedFieldsData.map((field, i) => {
                                  if (OPTIONAL_CALCULATION_FIELDS.includes(field.key) && !isOptionalFieldShown(field.key)) return null;

                                  const translatedName = getLabel(field.translation_key, field.name, field?.custom_name);
                                  let label = <label className="peb-field__label">{translatedName}</label>;

                                  let input = (
                                    <Field error={errors[field.key]?.message}>
                                      {field.key.includes("date") ? (
                                        <DateInput
                                          error={!!errors[field.key]}
                                          {...register((field.field_type === 'conditional' || field.field_type === 'system') ? `employee_information.${field.name}` : field.key)}
                                          maxLength={255}
                                        />
                                      ) : (
                                        <Input
                                          type="text"
                                          error={!!errors[field.key]}
                                          autoComplete="on"
                                          {...register((field.field_type === 'conditional' || field.field_type === 'system') ? `employee_information.${field.name}` : field.key)}
                                          maxLength={255}
                                        />
                                      )}
                                    </Field>
                                  );

                                  if (
                                    ["annual_compensation", "individual_ceiling"].includes(field.key)
                                  ) {
                                    input = (
                                      <>
                                        <InputAffix prefix="€" error={!!errors[field.key]} className="mb-3">
                                          <Input
                                            type="text"
                                            error={!!errors[field.key]}
                                            {...register(field.key)}
                                            maxLength={24}
                                          />
                                        </InputAffix>
                                        {errors[field.key] && (
                                          <div className="peb-field__error">
                                            {errors[field.key].message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  } else if (["pay_grade", "pay_grade_level"].includes(field.key)) {
                                    label = <label className="peb-field__label">{fieldCustomNames?.['field.pay_grade'] || editableTranslate('field.pay_grade')} / {fieldCustomNames?.['field.pay_grade_level'] || editableTranslate('field.pay_grade_level')}</label>;

                                    input = <PayFactorInput
                                      activePayfactorValues={payfactorCatalogValues}
                                      watchPayGrade={watchPayGrade}
                                      watchPayGradeLevel={watchPayGradeLevel}
                                      control={control}
                                      errors={errors}
                                      setValue={setValue}
                                    />;
                                  } else if (field.key === "working_hours") {
                                    input = (
                                      <>
                                        <InputAffix
                                          error={!!errors[field.key]}
                                          suffix={softwareSettings?.working_hours_type === "hours"
                                            ? editableTranslate("general_settings.working_hours_type.hours")
                                            : editableTranslate("general_settings.working_hours_type.percentage")}
                                        >
                                          <Input
                                            type="text"
                                            error={!!errors[field.key]}
                                            {...register(field.key)}
                                            maxLength={24}
                                          />
                                        </InputAffix>
                                        {errors["working_hours"] && (
                                          <div className="peb-field__error">
                                            {errors["working_hours"].message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  } else if (field.key === 'missing_months') {
                                    input = (
                                      <>
                                        <InputAffix
                                          error={!!errors[field.key]}
                                          className="mb-3"
                                          suffix={editableTranslate("form.months", "Monate")}
                                        >
                                          <Input
                                            type="text"
                                            error={!!errors[field.key]}
                                            {...register(field.key)}
                                            maxLength={7}
                                          />
                                        </InputAffix>
                                        {errors[field.key] && (
                                          <div className="peb-field__error">
                                            {errors[field.key].message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  } else if (field.key === 'missing_days' || field.key === 'working_days_per_week') {
                                    input = (
                                      <>
                                        <InputAffix
                                          error={!!errors[field.key]}
                                          className="mb-3"
                                          suffix={editableTranslate("form.days", "Tage")}
                                        >
                                          <Input
                                            type="text"
                                            error={!!errors[field.key]}
                                            {...register(field.key)}
                                            maxLength={365}
                                          />
                                        </InputAffix>
                                        {errors[field.key] && (
                                          <div className="peb-field__error">
                                            {errors[field.key].message}
                                          </div>
                                        )}
                                      </>
                                    );
                                  } else if (field.key === 'exclude_from_payout') {
                                    const translatedName = getLabel("field.manual_exclusion_checkbox", "Manual Exclusion");

                                    return <div key={i} className="col-12">
                                      <hr />
                                      <div className="form-group d-flex flex-wrap align-items-center">
                                        <Checkbox id={field.key} className="mr-4 mb-2" label={translatedName} data-testid="employee-field-exclude-from-payout" {...register(field.key)} />
                                        {optionalFieldToggles}
                                      </div>

                                      {
                                        watchExcludeFromPayout && <div className={`form-group mt-2 col-6 col-xl2-4 pl-0 pr-4`}>
                                          <Field label={editableTranslate("field.exclude_from_payout_reason", "Begründung")} required error={errors['exclude_from_payout_reason']?.message}>
                                            <Input
                                              type="text"
                                              error={!!errors['exclude_from_payout_reason']}
                                              {...register('exclude_from_payout_reason')}
                                              maxLength={50} data-testid="exclude-from-payout-reason-input"
                                            />
                                          </Field>
                                        </div>
                                      }
                                    </div>
                                  } else if (field.key === 'individual_pay_factor') {
                                    input = (
                                      <Field error={errors?.individual_pay_factor?.message}>
                                        <Input type="text" error={!!errors?.individual_pay_factor} {...register('individual_pay_factor')} maxLength={11} data-testid="individual-pay-factor-input" />
                                        {
                                          empty(watchIndividualPayFactor) && <small className="form-text text-muted ws-normal">{editableTranslate('employee.individual_pay_factor_empty_hint', lang === 'en' ? 'If left empty, the pay factor from the calculation settings applies.' : 'Ist das Feld leer, gilt der Entgeltfaktor aus den Berechnungseinstellungen.')}</small>
                                        }
                                      </Field>
                                    )
                                  }

                                  return <div key={i} className="col-6 col-xl2-4">
                                    <div className="form-group">
                                      {label}
                                      {input}
                                    </div>
                                  </div>
                                })}
                              {
                                !mergedCalculationRelatedFieldsData.some(f => f.key === 'exclude_from_payout') && optionalFieldToggles.length > 0 && <div className="col-12">
                                  <hr />
                                  <div className="form-group d-flex flex-wrap align-items-center">{optionalFieldToggles}</div>
                                </div>
                              }
                            </div>
                          }
                        </div>
                      </div>
                      {employee && !isDbModule(currentModule)
                        && !employee?.balanced_scorecards?.data?.some(Boolean)
                        && !employeeBalancedScorecards.some(Boolean)
                        && REFERENCE_FIELD_KEYS.some(key => !!employee[key]?.trim()) && (
                          <div className="employee-data-card-div mt-3" data-testid="first-scorecard-reference-data">
                            <div className="card-setting-header d-flex justify-content-between align-items-center">
                              <span>{editableTranslate('employee.first_scorecard_reference_data', 'Referenzdaten für die erste Leistungskarte')}</span>
                            </div>
                            <hr className="mt-2" />
                            <div className="card-setting-body mt-3 row">
                              {REFERENCE_FIELD_KEYS.map(key => (
                                <div key={key} className="col-6 col-xl2-4 d-flex justify-content-between p-0">
                                  <div className="col card-body-key">{editableTranslate(`field.${key}`)}</div>
                                  <div className="col card-body-value" data-testid={`reference-value-${key}`}>{employee[key] || '-'}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      {
                        freeFieldsData.length > 0 && <div className="employee-data-card-div mt-3" key={'additional_data'}>
                          <div className="card-setting-header d-flex justify-content-between align-items-center">
                            <span>{editableTranslate('employee.additional_data', 'Freifelder')}</span>
                          </div>
                          <hr className="mt-2" />
                          <div className="card-setting-body mt-3">
                            <div className="row">
                              {
                                !showEmployeeDataEditForm ? freeFieldsData.map((field, i) => {
                                  const translatedName = field?.custom_name || editableTranslate(field.translation_key, field.name);
                                  let val = employee?.information?.[field.name] || employee?.information?.[field.slug] || '-';

                                  return <div key={i} className="col-6 col-xl2-4 d-flex justify-content-between p-0">
                                    <div className="col card-body-key">{translatedName}</div>
                                    <div className="col card-body-value">{val}</div>
                                  </div>
                                }) : [...freeFieldsData].map((field, i) => {
                                  return (
                                    <div key={i} className="col-6 col-xl2-4">
                                      <div className="form-group">
                                        <Field
                                          label={field?.custom_name || editableTranslate(field.translation_key, field.name)}
                                          error={errors?.employee_information?.[field.name]?.message}
                                        >
                                          <Input
                                            type="text"
                                            error={!!errors?.employee_information?.[field.name]}
                                            {...register(`employee_information.${field.name}`)}
                                            maxLength={255}
                                          />
                                        </Field>
                                      </div>
                                    </div>
                                  );
                                })
                              }
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  </form>
                </div>
              </div>

              {
                employee && <div className="col-auto employee-balancescorecard-card-col">
                  <div className="h-100">
                    <div className={`employee-data-card ` + (employee.exclude_from_payout ? ' exclude-from-payout' : '')}>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h4 className="mb-0">{editableTranslate(employeeBalancedScorecards.length > 1 ? 'employee.balanced_scorecards' : 'employee.balanced_scorecard')}
                        </h4>
                        {
                          !employee.exclude_from_payout && <Button variant="secondary" className={`add-balanced-scorecard-btn ml-3 ${(employeeBalancedScorecards.length < 3 && hasPermissionTo('employee_management.manage_balanced_scorecards.create', permissions)) ? '' : 'invisible'} `} onClick={handleAddBalancedScorecard} data-testid="add-scorecard-button"><i className="bi bi-plus text-normal"></i> {editableTranslate('employee_management.add_balanced_scorecard', 'Hinzufügen')}</Button>
                        }
                      </div>

                      {
                        (employeeBalancedScorecards.length > 0 && !hasAddLkForm && lkWeightTotal !== 100) && <div className="alert alert-danger">
                          {editableTranslate('employee_management.balanced_scorecards_total_weight_error')}
                        </div>
                      }

                      {
                        (employeeBalancedScorecards.length > 0 && showSecondLkAlert) && <div className="alert alert-warning d-flex justify-content-between">
                          <div className="mr-1">{editableTranslate('employee_management.balanced_scorecards_weighting_auto_subtract', "Die Gewichtung der ersten Leistungskarte wurde automatisch angepasst, um eine Gesamtgewichtung von 100 % sicherzustellen.")}</div>

                          <Button variant="ghost" size="inline" icon="bi-x" className="close" onClick={() => setShowSecondLkAlert(false)} data-testid="close-button" />
                        </div>
                      }

                      {
                        (employeeBalancedScorecards.length > 0 && showDeleteLkAlert) && <div className="alert alert-warning d-flex justify-content-between">
                          <div className="mr-1">{editableTranslate('employee_management.balanced_scorecards_weighting_on_delete')}</div>

                          <Button variant="ghost" size="inline" icon="bi-x" className="close" onClick={() => setShowDeleteLkAlert(false)} data-testid="close-button" />
                        </div>
                      }

                      {
                        employeeBalancedScorecards.map((bs, i) => <BalancedScorecard key={i} index={i} employee={employee} data={bs} total={employeeBalancedScorecards.length} fetchEmployees={fetchEmployees} balancedScorecardOpened={balancedScorecardOpened} setBalancedScorecardOpened={setBalancedScorecardOpened} handleCancelAddBalancedScorecard={handleCancelAddBalancedScorecard} permissions={permissions} setEmployeeLkDirty={setEmployeeLkDirty} getLabel={getLabel} lkWeightTotal={lkWeightTotal} setShowSecondLkAlert={setShowSecondLkAlert} setShowDeleteLkAlert={setShowDeleteLkAlert} isInternalTenant={isInternalTenant} />)
                      }

                      <ReactTooltip id={`lk-last-updated-tooltip`} place="bottom" />
                      <ReactTooltip id={`complaint-resolved-tooltip`} place="bottom" />
                      <ReactTooltip id={`complaint-unlock`} place="bottom" content={translate('tooltips.complaint.unlock', 'Entsperren')} />
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>

          {
            !employee && <hr className="mb-0" />
          }

          <EmployeeDeactivationModal
            show={showDeactivateModal}
            loading={loadingDeactivate}
            employeeCount={1}
            finalAssignmentCount={finalAssignmentCount}
            requiresFinalAssignmentConfirmation={finalAssignmentCount !== null}
            onCancel={closeDeactivateModal}
            onConfirm={() => handleEmployeeDeactivate(finalAssignmentCount !== null)}
            testId="employee-deactivation"
          />
        </td>
      </tr>
    }
  </>
}
